<script>
import { pipelineAPI } from '../../../api/pipeline';
import SearchAPI from '../../../api/search';
import ConversationAPI from '../../../api/inbox/conversation';
import { useAlert } from 'dashboard/composables';
import { mapGetters } from 'vuex';
import {
  serveNoKanban,
  etiquetasParaEscolher,
} from 'dashboard/helper/escopoDaEtiqueta';
import FullEditor from 'dashboard/components/widgets/WootWriter/FullEditor.vue';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import draggable from 'vuedraggable';

const DESCRIPTION_MENU_OPTIONS = ['strong', 'em', 'link', 'undo', 'redo', 'bulletList', 'orderedList', 'h2', 'h3'];

export default {
  name: 'KanbanIndex',
  components: { FullEditor, draggable },
  setup() {
    const { formatMessage } = useMessageFormatter();
    return { formatMessage, DESCRIPTION_MENU_OPTIONS };
  },
  data() {
    return {
      loading: true,
      sidebarOpen: true,
      pipelines: [],
      current: null,
      cards: {},
      // Bolinhas de não lidas: { display_id: mensagens } e [{ cardId, stageId, conversationIds }]
      naoLidasPorConversa: {},
      naoLidasCards: [],
      pipeModal: false,
      pipeForm: {},
      stageModal: false,
      stageForm: {},
      cardModal: false,
      cardForm: {},
      // Transferência de card para outro funil/etapa (modal único, 2 dropdowns)
      transferModal: false,
      transferCard: null,
      transferPipelineId: null,
      transferStageId: null,
      transferStages: [],
      transferLoadingStages: false,
      transferSubmitting: false,
      timelineModal: null,
      timeline: [],
      timelineExpanded: {},

      // agent dropdown
      agentSearch: '',
      showAgentDropdown: false,
      showParticipantDropdown: false,

      // contact search
      contactSearch: '',
      contactResults: [],
      contactSearching: false,

      // conversation search
      conversationSearch: '',
      conversationResults: [],
      conversationSearching: false,

      // products
      catalogProducts: [],
      productPickId: null,
      productPickQty: 1,
      productPickDiscount: 0,

      // accordion sections no modal do card
      sectionOpen: { descricao: true, oportunidade: false, relacionamentos: false, agenda: false },

      // drag-and-drop entre etapas
      draggingCardStageId: null,

      // filtros do board
      filters: { q: '', priority: '', assigneeId: null },
      filterDebounceTimer: null,

      // menu "···" de ações
      moreMenuOpen: false,

      // Quais cards o quadro mostra: 'abertos' | 'fechados' (ganhos + perdidos) | 'todos'
      situacaoCards: 'abertos',
      // Quantos cards de cada etapa batem com os filtros (o número do topo da coluna)
      totaisPorEtapa: {},

      // Webhooks (configurados no modal de settings)
      webhooks: [],
      webhookNewUrl: '',
      webhookNewDesc: '',
      webhookNewEvents: ['card.created', 'card.updated', 'card.stage_changed', 'card.won', 'card.lost', 'card.overdue'],
      allWebhookEvents: ['card.created', 'card.updated', 'card.stage_changed', 'card.won', 'card.lost', 'card.overdue'],

      // relatórios
      metricsModal: false,
      metrics: null,
      metricsLoading: false,
      metricsFrom: '',
      metricsTo: '',
      metricsExporting: false,

      // gestão do catálogo de produtos
      productMgmtModal: false,
      productMgmtForm: { name: '', description: '', unitPrice: 0 },
      productMgmtEditId: null,
      // ---- Settings do pipeline
      settingsModal: false,
      settingsForm: {
        automations: {
          autoCreateCard: false,
          autoAssignAgent: false,
          autoMoveOnResolve: false,
          autoMoveOnReopen: false,
          notifyAgentOnAssign: false,
          notifyOnCardOverdue: false,
          notifyOnStageChange: false,
          showClosedConversations: false,
        },
        inboxIds: [],
      },
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      currentUser: 'getCurrentUser',
      agents: 'agents/getAgents',
      inboxes: 'inboxes/getInboxes',
      accountLabels: 'labels/getLabels',
    }),
    userId() {
      return this.currentUser?.id;
    },
    // Etiquetas do formulário do card: as do Kanban + as que o card já tem.
    kanbanLabels() {
      return etiquetasParaEscolher(
        this.accountLabels,
        serveNoKanban,
        this.cardForm.selectedLabels || []
      );
    },
    filteredAgentsForAssignee() {
      const selected = this.cardForm.assigneeIds || [];
      const q = this.agentSearch.toLowerCase();
      return this.agents.filter(
        a => !selected.includes(a.id) && (!q || a.name.toLowerCase().includes(q))
      );
    },
    filteredAgentsForParticipant() {
      const selected = this.cardForm.participantIds || [];
      const q = this.agentSearch.toLowerCase();
      return this.agents.filter(
        a => !selected.includes(a.id) && (!q || a.name.toLowerCase().includes(q))
      );
    },
    selectedAssignees() {
      return (this.cardForm.assigneeIds || [])
        .map(id => this.agents.find(a => a.id === id))
        .filter(Boolean);
    },
    selectedParticipants() {
      return (this.cardForm.participantIds || [])
        .map(id => this.agents.find(a => a.id === id))
        .filter(Boolean);
    },
    productsTotal() {
      return (this.cardForm.selectedProducts || []).reduce((sum, item) => {
        const qty = parseFloat(item.quantity) || 0;
        const price = parseFloat(item.unitPrice) || 0;
        const disc = parseFloat(item.discountPercentage) || 0;
        return sum + qty * price * (1 - disc / 100);
      }, 0);
    },
    activeFiltersCount() {
      return (this.filters.q.trim() ? 1 : 0) +
             (this.filters.priority ? 1 : 0) +
             (this.filters.assigneeId ? 1 : 0);
    },
    hasFilters() {
      return this.activeFiltersCount > 0;
    },
    // Mensagens não lidas por etapa: soma todos os cards da etapa, não só os carregados.
    naoLidasPorEtapa() {
      const porEtapa = {};
      this.naoLidasCards.forEach(({ stageId, conversationIds }) => {
        const soma = conversationIds.reduce(
          (total, id) => total + this.naoLidasDaConversa(id),
          0
        );
        porEtapa[stageId] = (porEtapa[stageId] || 0) + soma;
      });
      return porEtapa;
    },
  },
  async mounted() {
    await Promise.all([
      this.$store.dispatch('agents/get'),
      this.$store.dispatch('labels/get'),
    ]);
    await this.fetchOverview();
    const cardId = Number(this.$route?.query?.cardId);
    if (cardId) this.autoOpenCard(cardId);
    // As bolinhas do menu já ouvem o tempo real e revisam a cada minuto: o quadro
    // se atualiza junto, a cada nova contagem.
    this.pararDeOuvirNaoLidas = this.$store.subscribe(({ type }) => {
      if (type === 'conversasNaoLidas/SET_CONTAGENS') this.agendarNaoLidas();
    });
  },
  beforeUnmount() {
    this.pararDeOuvirNaoLidas?.();
    clearTimeout(this.naoLidasTimer);
  },
  methods: {
    money(v, cur) {
      const value = v == null ? 0 : Number(v);
      try {
        return new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: cur || 'BRL',
        }).format(value);
      } catch {
        return `${cur || 'BRL'} ${value}`;
      }
    },
    fmt(d) {
      return d ? new Date(d).toLocaleString('pt-BR') : '';
    },
    agentInitials(agent) {
      if (!agent?.name) return '?';
      return agent.name
        .split(' ')
        .slice(0, 2)
        .map(w => w[0])
        .join('')
        .toUpperCase();
    },

    // ---- Funis ----
    async fetchOverview() {
      this.loading = true;
      try {
        const { data } = await pipelineAPI.getOverview(this.accountId, this.userId);
        this.pipelines = data;
      } catch {
        useAlert('Erro ao carregar os funis do Pipeline.');
      } finally {
        this.loading = false;
      }
    },
    async openPipeline(id) {
      try {
        const { data } = await pipelineAPI.getSummary(id, this.accountId, this.userId);
        this.current = data;
        this.cards = {};
        this.naoLidasCards = [];
        await this.applyFilters();
      } catch (e) {
        console.error('[openPipeline] error:', e);
        useAlert('Erro ao abrir o funil.');
      }
    },
    async applyFilters() {
      if (!this.current) return;
      const base = { ...this.filtrosDoQuadro(), size: 50 };
      const newCards = {};
      const totais = {};
      await Promise.all(
        this.current.stages.map(async st => {
          const { data: cd } = await pipelineAPI.getCards(this.accountId, { ...base, stageId: st.id });
          newCards[st.id] = cd.cards;
          totais[st.id] = cd.meta?.totalCount ?? cd.cards.length;
        })
      );
      this.cards = newCards;
      this.totaisPorEtapa = totais;
      this.atualizarNaoLidas();
    },
    filtrosDoQuadro() {
      const filtros = { pipelineId: this.current.id };
      if (this.filters.q.trim()) filtros.q = this.filters.q.trim();
      if (this.filters.priority) filtros.priority = this.filters.priority;
      if (this.filters.assigneeId) filtros.assigneeId = this.filters.assigneeId;
      if (this.situacaoCards === 'abertos') filtros.status = 'OPEN';
      if (this.situacaoCards === 'fechados') filtros.status = 'CLOSED';
      return filtros;
    },

    // ---- Número do topo da coluna ----
    // Conta só o que a coluna mostra (filtros + Abertos/Fechados/Todos). O card_count da
    // etapa inclui ganhos e perdidos, que ficam escondidos: era o "número sem card".
    contadorDaEtapa(st) {
      return this.totaisPorEtapa[st.id] ?? (this.cards[st.id] || []).length;
    },
    tituloDoContador(st) {
      const rotulo = { abertos: 'abertos', fechados: 'fechados', todos: 'no total' }[this.situacaoCards];
      const filtrado = this.hasFilters ? ' (com filtros)' : '';
      return `${this.contadorDaEtapa(st)} cards ${rotulo}${filtrado} · ${st.cardCount} na etapa contando fechados`;
    },

    // ---- Bolinhas de não lidas ----
    naoLidasDaConversa(convId) {
      return this.naoLidasPorConversa[convId] || 0;
    },
    tituloDaConversa(convId) {
      const naoLidas = this.naoLidasDaConversa(convId);
      return naoLidas
        ? `Abrir conversa #${convId} (${naoLidas} mensagens não lidas)`
        : `Abrir conversa #${convId}`;
    },
    textoNaoLidas(quantidade) {
      return quantidade > 99 ? '99+' : String(quantidade);
    },
    agendarNaoLidas() {
      clearTimeout(this.naoLidasTimer);
      this.naoLidasTimer = setTimeout(() => this.atualizarNaoLidas(), 500);
    },
    async atualizarNaoLidas() {
      if (!this.current) return;
      const funil = this.current.id;
      try {
        const { data: porConversa } = await ConversationAPI.unreadByConversation();
        const ids = Object.keys(porConversa || {}).map(Number);
        let cards = [];
        if (ids.length) {
          const { data } = await pipelineAPI.getCardsByConversations(
            this.accountId,
            ids,
            this.filtrosDoQuadro()
          );
          cards = data || [];
        }
        if (this.current?.id !== funil) return;
        this.naoLidasPorConversa = porConversa || {};
        this.naoLidasCards = cards;
      } catch {
        // Sem a contagem, as bolinhas só não aparecem; o quadro continua funcionando.
      }
    },
    clearFilters() {
      this.filters = { q: '', priority: '', assigneeId: null };
      this.applyFilters();
    },
    onFilterQChange() {
      clearTimeout(this.filterDebounceTimer);
      this.filterDebounceTimer = setTimeout(() => this.applyFilters(), 400);
    },

    openPipelineModal() {
      this.pipeForm = { name: '', currency: 'BRL' };
      this.pipeModal = true;
    },
    async savePipeline() {
      try {
        await pipelineAPI.createPipeline(this.pipeForm, this.accountId);
        this.pipeModal = false;
        useAlert('Funil criado.');
        await this.fetchOverview();
      } catch {
        useAlert('Erro ao criar o funil.');
      }
    },
    async toggleFav(id) {
      try {
        await pipelineAPI.toggleFavorite(id, this.accountId, this.userId);
        await this.fetchOverview();
        if (this.current) await this.openPipeline(this.current.id);
      } catch {
        useAlert('Erro ao favoritar.');
      }
    },

    // ---- Etapas ----
    openStageModal(st) {
      this.stageForm = st
        ? { ...st }
        : { name: '', color: '#670961', winProbability: 100, isWon: false, isLost: false };
      this.stageModal = true;
    },
    async saveStage() {
      const body = {
        name: this.stageForm.name,
        color: this.stageForm.color,
        winProbability: this.stageForm.winProbability,
        isWon: this.stageForm.isWon,
        isLost: this.stageForm.isLost,
      };
      try {
        if (this.stageForm.id) {
          await pipelineAPI.updateStage(this.current.id, this.stageForm.id, body, this.accountId);
        } else {
          await pipelineAPI.createStage(this.current.id, body, this.accountId);
        }
        this.stageModal = false;
        useAlert('Etapa salva.');
        await this.openPipeline(this.current.id);
        await this.fetchOverview();
      } catch (e) {
        useAlert(e?.response?.data?.error || 'Erro ao salvar a etapa.');
      }
    },
    async delStage() {
      if (!window.confirm('Excluir esta etapa?')) return;
      try {
        await pipelineAPI.deleteStage(this.current.id, this.stageForm.id, this.accountId);
        this.stageModal = false;
        useAlert('Etapa excluída.');
        await this.openPipeline(this.current.id);
        await this.fetchOverview();
      } catch (e) {
        useAlert(e?.response?.data?.error || 'Erro ao excluir a etapa.');
      }
    },

    // ---- Cards ----
    openCardModal(stageId, c) {
      this.agentSearch = '';
      this.showAgentDropdown = false;
      this.showParticipantDropdown = false;
      this.contactSearch = '';
      this.contactResults = [];
      this.conversationSearch = '';
      this.conversationResults = [];
      this.productPickId = null;
      this.productPickQty = 1;
      this.productPickDiscount = 0;
      this.sectionOpen = { descricao: true, oportunidade: false, relacionamentos: false, agenda: false };

      if (c) {
        this.cardForm = {
          id: c.id,
          stageId: c.stageId,
          status: c.status || 'OPEN',
          title: c.title,
          description: c.description || '',
          priority: c.priority,
          value: c.value,
          selectedLabels: c.labels || [],
          startDate: c.startDate ? c.startDate.substring(0, 10) : '',
          startTime: c.startDate ? c.startDate.substring(11, 16) : '00:00',
          dueDate: c.dueDate ? c.dueDate.substring(0, 10) : '',
          dueTime: c.dueDate ? c.dueDate.substring(11, 16) : '00:00',
          assigneeIds: c.assigneeIds || [],
          participantIds: c.participantIds || [],
          selectedContacts: c.contacts?.length
            ? c.contacts.map(ct => ({ id: ct.id, name: ct.name || `Contato #${ct.id}`, email: ct.email || null, phone: ct.phone || null }))
            : (c.contactIds || []).map(id => ({ id, name: `Contato #${id}`, email: null, phone: null })),
          selectedConversations: c.conversations?.length
            ? c.conversations.map(cv => ({
                id: cv.id,
                displayId: cv.displayId ?? null,
                inboxId: cv.inboxId ?? null,
                label: `#${cv.displayId ?? cv.id}`,
              }))
            : (c.conversationIds || []).map(id => ({ id, label: `#${id}` })),
          selectedProducts: [],
          originalProductItemIds: [],
        };
        this.loadCardProducts(c.id);
      } else {
        this.cardForm = {
          stageId: stageId || this.current.stages[0]?.id,
          title: '',
          description: '',
          priority: null,
          value: null,
          selectedLabels: [],
          startDate: '',
          startTime: '00:00',
          dueDate: '',
          dueTime: '00:00',
          assigneeIds: [],
          participantIds: [],
          selectedContacts: [],
          selectedConversations: [],
          selectedProducts: [],
          originalProductItemIds: [],
        };
      }
      this.loadCatalogProducts();
      this.cardModal = true;
    },
    async autoOpenCard(cardId) {
      // Abre o primeiro pipeline que contenha o card, então abre o modal
      for (const pipeline of (this.pipelines || [])) {
        try {
          await this.openPipeline(pipeline.id);
          for (const stageCards of Object.values(this.cards)) {
            const found = stageCards.find(c => c.id === cardId);
            if (found) {
              this.openCardModal(found.stageId, found);
              return;
            }
          }
        } catch {}
      }
    },
    async saveCard() {
      const labels = this.cardForm.selectedLabels || [];
      const body = {
        pipelineId: this.current.id,
        stageId: this.cardForm.stageId,
        title: this.cardForm.title,
        description: this.cardForm.description,
        priority: this.cardForm.priority,
        value: this.cardForm.value,
        labels,
        startDate: this.cardForm.startDate
          ? `${this.cardForm.startDate}T${this.cardForm.startTime || '00:00'}:00`
          : null,
        dueDate: this.cardForm.dueDate
          ? `${this.cardForm.dueDate}T${this.cardForm.dueTime || '00:00'}:00`
          : null,
        assignees: (this.cardForm.assigneeIds || []).map(id => {
          const agent = this.agents.find(a => a.id === id);
          return { id, name: agent?.name ?? null, email: agent?.email ?? null };
        }),
        participantIds: this.cardForm.participantIds || [],
        contacts: (this.cardForm.selectedContacts || []).map(c => ({
          id: c.id,
          name: c.name ?? null,
          email: c.email ?? null,
          phone: c.phone ?? null,
        })),
        conversations: (this.cardForm.selectedConversations || []).map(c => ({
          id: c.id,
          displayId: c.displayId || null,
          inboxId: c.inboxId || null,
        })),
      };
      try {
        let resp;
        if (this.cardForm.id) {
          resp = await pipelineAPI.updateCard(this.cardForm.id, body, this.accountId, this.userId);
        } else {
          resp = await pipelineAPI.createCard(body, this.accountId, this.userId);
        }
        const saved = resp?.data;

        // Sincroniza produtos: remove os anteriores e adiciona os atuais
        if (saved?.id) {
          await this.syncCardProducts(saved.id);
        }

        this.cardModal = false;
        useAlert('Card salvo.');

        // Atualiza o card diretamente no estado local para feedback imediato
        if (saved?.id && saved?.stageId != null) {
          const stageCards = this.cards[saved.stageId] || [];
          const idx = stageCards.findIndex(c => c.id === saved.id);
          if (idx >= 0) {
            const updated = [...stageCards];
            updated[idx] = saved;
            this.cards = { ...this.cards, [saved.stageId]: updated };
          }
        }

        // Sincroniza tudo em background (recarrega contagens, cards de outras etapas etc.)
        await this.openPipeline(this.current.id);
        await this.fetchOverview();
      } catch (e) {
        console.error('[saveCard] error:', e);
        useAlert(e?.response?.data?.error || 'Erro ao salvar o card.');
      }
    },
    async delCard(c) {
      if (!window.confirm('Excluir este card?')) return;
      try {
        await pipelineAPI.deleteCard(c.id, this.accountId);
        useAlert('Card excluído.');
        await this.openPipeline(this.current.id);
        await this.fetchOverview();
      } catch {
        useAlert('Erro ao excluir o card.');
      }
    },

    // ---- Mover ----
    canMove(st, dir) {
      const i = this.current.stages.findIndex(s => s.id === st.id);
      const j = i + dir;
      return j >= 0 && j < this.current.stages.length;
    },
    async move(c, dir) {
      const i = this.current.stages.findIndex(s => s.id === c.stageId);
      const target = this.current.stages[i + dir];
      if (!target) return;
      try {
        await pipelineAPI.moveCard(c.id, { stageId: target.id }, this.accountId, this.userId);
        await this.openPipeline(this.current.id);
        await this.fetchOverview();
      } catch {
        useAlert('Erro ao mover o card.');
      }
    },

    // ---- Transferência de card para outro funil (modal único, 2 dropdowns) ----
    transferTargets() {
      // Funis ativos, exceto o atual.
      return (this.pipelines || []).filter(p => !this.current || p.id !== this.current.id);
    },
    openTransfer(card) {
      this.transferCard = card;
      this.transferPipelineId = null;
      this.transferStageId = null;
      this.transferStages = [];
      this.transferLoadingStages = false;
      this.transferSubmitting = false;
      this.transferModal = true;
    },
    closeTransfer() {
      this.transferModal = false;
      this.transferCard = null;
    },
    // Dropdown 1 (funil) mudou: carrega as etapas que alimentam o dropdown 2.
    async onTransferPipelineChange() {
      this.transferStageId = null;
      this.transferStages = [];
      if (!this.transferPipelineId) return;
      this.transferLoadingStages = true;
      try {
        const { data } = await pipelineAPI.getStages(this.transferPipelineId, this.accountId);
        this.transferStages = data || [];
      } catch {
        useAlert('Erro ao carregar as etapas do funil de destino.');
      } finally {
        this.transferLoadingStages = false;
      }
    },
    selectedTransferStage() {
      return (this.transferStages || []).find(s => s.id === this.transferStageId) || null;
    },
    stagePlaceholder() {
      if (!this.transferPipelineId) return 'Selecione o funil primeiro…';
      if (this.transferLoadingStages) return 'Carregando etapas…';
      if (!this.transferStages.length) return 'Este funil não tem etapas';
      return 'Selecione uma etapa…';
    },
    async confirmTransfer() {
      if (!this.transferCard || !this.transferPipelineId || !this.transferStageId || this.transferSubmitting) return;
      this.transferSubmitting = true;
      try {
        await pipelineAPI.moveCard(
          this.transferCard.id,
          { pipelineId: this.transferPipelineId, stageId: this.transferStageId },
          this.accountId,
          this.userId
        );
        this.closeTransfer();
        useAlert('Card transferido com sucesso.');
        // Recarrega o funil atual (o card sai do board) e a contagem dos funis.
        await this.applyFilters();
        await this.fetchOverview();
      } catch {
        useAlert('Erro ao transferir o card.');
      } finally {
        this.transferSubmitting = false;
      }
    },

    onCardDragStart(stageId) {
      this.draggingCardStageId = stageId;
    },
    async onCardDropIntoStage(toStageId, event) {
      const fromStageId = this.draggingCardStageId;
      this.draggingCardStageId = null;
      if (!fromStageId || fromStageId === toStageId) return;
      const card = (this.cards[toStageId] || [])[event.newIndex];
      if (!card) return;
      try {
        const { data: movido } = await pipelineAPI.moveCard(card.id, { stageId: toStageId }, this.accountId, this.userId);
        card.stageId = toStageId;
        this.ajustarTotal(fromStageId, -1);
        this.ajustarTotal(toStageId, +1);
        const comNaoLidas = this.naoLidasCards.find(n => n.cardId === card.id);
        if (comNaoLidas) comNaoLidas.stageId = toStageId;
        this.tirarSeSaiuDaVista(card, toStageId, movido?.status);
        await this.fetchOverview();
      } catch {
        await this.openPipeline(this.current.id);
        useAlert('Erro ao mover o card.');
      }
    },

    ajustarTotal(stageId, delta) {
      if (this.totaisPorEtapa[stageId] === undefined) return;
      this.totaisPorEtapa = {
        ...this.totaisPorEtapa,
        [stageId]: Math.max(0, this.totaisPorEtapa[stageId] + delta),
      };
    },
    // Etapa marcada como Ganho/Perdido fecha o card ao recebê-lo. Na vista "Abertos" ele
    // sumiria só no próximo recarregamento — tira já e avisa onde encontrá-lo.
    tirarSeSaiuDaVista(card, stageId, status) {
      if (!status || status === card.status) return;
      card.status = status;
      const fechado = status === 'WON' || status === 'LOST';
      const saiu = (this.situacaoCards === 'abertos' && fechado) ||
                   (this.situacaoCards === 'fechados' && !fechado);
      if (!saiu) return;
      this.cards = { ...this.cards, [stageId]: (this.cards[stageId] || []).filter(x => x.id !== card.id) };
      this.ajustarTotal(stageId, -1);
      useAlert(fechado
        ? `Esta etapa fecha o card como ${status === 'WON' ? 'Ganho' : 'Perdido'}. Ele aparece em "Fechados".`
        : 'O card foi reaberto. Ele aparece em "Abertos".');
    },

    // ---- Timeline ----
    async showTimeline(c) {
      try {
        const { data } = await pipelineAPI.getCardEvents(c.id, this.accountId);
        this.timeline = data;
        this.timelineModal = c;
        this.timelineExpanded = {};
      } catch {
        useAlert('Erro ao carregar a timeline.');
      }
    },
    toggleEventDetail(eventId) {
      this.timelineExpanded = { ...this.timelineExpanded, [eventId]: !this.timelineExpanded[eventId] };
    },
    actionLabel(action) {
      const map = {
        'card.created': 'Card Criado',
        'card.updated': 'Card Alterado',
        'card.stage_changed': 'Card Movimentado',
        'card.won': 'Card Ganho',
        'card.lost': 'Card Perdido',
        'card.deleted': 'Card Excluído',
      };
      return map[action] || action;
    },
    actionIcon(action) {
      const map = {
        'card.created': '✨',
        'card.updated': '✏️',
        'card.stage_changed': '↔️',
        'card.won': '✅',
        'card.lost': '❌',
        'card.deleted': '🗑️',
      };
      return map[action] || '📋';
    },
    actionColor(action) {
      const map = {
        'card.created': 'border-l-green-400',
        'card.updated': 'border-l-blue-400',
        'card.stage_changed': 'border-l-purple-400',
        'card.won': 'border-l-green-600',
        'card.lost': 'border-l-red-400',
      };
      return map[action] || 'border-l-n-weak';
    },
    hasEventDetails(e) {
      return ['card.updated', 'card.stage_changed'].includes(e.action);
    },
    stageName(stageId) {
      if (!stageId || !this.current) return `Etapa #${stageId || '?'}`;
      const stage = this.current.stages.find(s => s.id === Number(stageId));
      return stage ? stage.name : `Etapa #${stageId}`;
    },

    // ---- Agentes (assignees) ----
    toggleAssignee(agentId) {
      const ids = this.cardForm.assigneeIds || [];
      this.cardForm.assigneeIds = ids.includes(agentId)
        ? ids.filter(id => id !== agentId)
        : [...ids, agentId];
    },
    assignToMe() {
      const id = this.currentUser?.id;
      if (!id) return;
      if (!(this.cardForm.assigneeIds || []).includes(id)) {
        this.cardForm.assigneeIds = [...(this.cardForm.assigneeIds || []), id];
      }
      this.showAgentDropdown = false;
    },
    removeAssignee(agentId) {
      this.cardForm.assigneeIds = (this.cardForm.assigneeIds || []).filter(id => id !== agentId);
    },

    // ---- Participantes ----
    toggleParticipant(agentId) {
      const ids = this.cardForm.participantIds || [];
      this.cardForm.participantIds = ids.includes(agentId)
        ? ids.filter(id => id !== agentId)
        : [...ids, agentId];
      this.showParticipantDropdown = false;
    },
    removeParticipant(agentId) {
      this.cardForm.participantIds = (this.cardForm.participantIds || []).filter(id => id !== agentId);
    },

    // ---- Contatos ----
    async onContactSearch() {
      const q = this.contactSearch.trim();
      if (q.length < 2) { this.contactResults = []; return; }
      this.contactSearching = true;
      try {
        const { data } = await SearchAPI.contacts({ q });
        this.contactResults = data.payload?.contacts || [];
      } catch {
        this.contactResults = [];
      } finally {
        this.contactSearching = false;
      }
    },
    addContact(contact) {
      const contacts = this.cardForm.selectedContacts || [];
      if (!contacts.find(c => c.id === contact.id)) {
        this.cardForm.selectedContacts = [
          ...contacts,
          {
            id: contact.id,
            name: contact.name || `Contato #${contact.id}`,
            email: contact.email ?? null,
            phone: contact.phone_number ?? null,
          },
        ];
      }
      this.contactSearch = '';
      this.contactResults = [];
    },
    removeContact(id) {
      this.cardForm.selectedContacts = (this.cardForm.selectedContacts || []).filter(c => c.id !== id);
    },

    // ---- Conversas ----
    async onConversationSearch() {
      const q = this.conversationSearch.trim();
      if (q.length < 1) { this.conversationResults = []; return; }
      this.conversationSearching = true;
      try {
        const { data } = await SearchAPI.conversations({ q });
        this.conversationResults = data.payload?.conversations || [];
      } catch {
        this.conversationResults = [];
      } finally {
        this.conversationSearching = false;
      }
    },
    addConversation(conv) {
      const convs = this.cardForm.selectedConversations || [];
      // id interno (PK da conversa) é a chave de persistência; display_id é só exibição/rota.
      // A busca expõe conversation_id (interno) e id (= display_id). Fallback p/ display se ausente.
      const internalId = conv.conversation_id ?? conv.id;
      const displayId = conv.display_id ?? conv.id;
      if (!convs.find(c => c.id === internalId)) {
        const contactName = conv.contact?.name || '';
        const phone = conv.contact?.phone_number || '';
        const detail = contactName || phone;
        this.cardForm.selectedConversations = [
          ...convs,
          {
            id: internalId,
            displayId,
            inboxId: conv.inbox?.id ?? conv.inbox_id ?? null,
            label: detail ? `#${displayId} — ${detail}` : `#${displayId}`,
          },
        ];
      }
      this.conversationSearch = '';
      this.conversationResults = [];
    },
    removeConversation(id) {
      this.cardForm.selectedConversations = (this.cardForm.selectedConversations || []).filter(c => c.id !== id);
    },

    goToConversation(convId) {
      this.$router.push(`/app/accounts/${this.accountId}/conversations/${convId}`);
    },

    isCardOverdue(c) {
      return c.dueDate && new Date(c.dueDate) < new Date() && (!c.status || c.status === 'OPEN');
    },

    dueDateLabel(dueDate) {
      if (!dueDate) return '';
      const due = new Date(dueDate);
      const now = new Date();
      const diffMs = due - now;
      const d = due.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
      const t = due.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
      if (diffMs < 0) return `Vencido ${d} ${t}`;
      if (due.toDateString() === now.toDateString()) return `Vence hoje às ${t}`;
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      if (due.toDateString() === tomorrow.toDateString()) return `Amanhã ${d} às ${t}`;
      return `Vence ${d} às ${t}`;
    },
    dueDateClass(dueDate) {
      if (!dueDate) return '';
      const due = new Date(dueDate);
      const now = new Date();
      if (due < now) return 'bg-red-50 text-red-700 border-red-300';
      if (due.toDateString() === now.toDateString()) return 'bg-yellow-50 text-yellow-700 border-yellow-300';
      return 'bg-green-50 text-green-700 border-green-300';
    },
    dueDateTitle(dueDate) {
      if (!dueDate) return '';
      const due = new Date(dueDate);
      const now = new Date();
      const diffMs = due - now;
      if (diffMs < 0) {
        const h = Math.abs(Math.floor(diffMs / 3600000));
        return h < 24 ? `Vencido há ${h}h` : `Vencido há ${Math.floor(h / 24)} dia(s)`;
      }
      if (due.toDateString() === now.toDateString()) {
        const h = Math.floor(diffMs / 3600000);
        const m = Math.floor((diffMs % 3600000) / 60000);
        return h > 0 ? `Vence em ${h}h ${m}min` : `Vence em ${m}min`;
      }
      return `Vence em ${Math.ceil(diffMs / 86400000)} dia(s)`;
    },

    // ---- Catálogo de produtos (gestão) ----
    async openProductMgmt() {
      await this.loadCatalogProducts();
      this.productMgmtForm = { name: '', description: '', unitPrice: 0 };
      this.productMgmtEditId = null;
      this.productMgmtModal = true;
    },
    editProductMgmt(product) {
      this.productMgmtEditId = product.id;
      this.productMgmtForm = {
        name: product.name,
        description: product.description || '',
        unitPrice: parseFloat(product.unitPrice) || 0,
      };
    },
    cancelProductMgmtEdit() {
      this.productMgmtEditId = null;
      this.productMgmtForm = { name: '', description: '', unitPrice: 0 };
    },
    async saveProductMgmt() {
      if (!this.productMgmtForm.name.trim()) return;
      const payload = {
        name: this.productMgmtForm.name.trim(),
        description: this.productMgmtForm.description.trim(),
        unitPrice: parseFloat(this.productMgmtForm.unitPrice) || 0,
      };
      try {
        if (this.productMgmtEditId) {
          await pipelineAPI.updateProduct(this.current.id, this.productMgmtEditId, payload, this.accountId);
        } else {
          await pipelineAPI.createProduct(this.current.id, payload, this.accountId);
        }
        await this.loadCatalogProducts();
        this.productMgmtForm = { name: '', description: '', unitPrice: 0 };
        this.productMgmtEditId = null;
        useAlert('Produto salvo.');
      } catch {
        useAlert('Erro ao salvar produto.');
      }
    },
    async deleteProductMgmt(productId) {
      if (!window.confirm('Excluir este produto do catálogo?')) return;
      try {
        await pipelineAPI.deleteProduct(this.current.id, productId, this.accountId);
        await this.loadCatalogProducts();
        if (this.productMgmtEditId === productId) this.cancelProductMgmtEdit();
        useAlert('Produto excluído.');
      } catch {
        useAlert('Erro ao excluir produto.');
      }
    },

    // ---- Relatórios ----
    async openMetrics() {
      if (!this.current) return;
      this.metricsModal = true;
      this.metricsLoading = true;
      this.metrics = null;
      try {
        const { data } = await pipelineAPI.getMetrics(
          this.current.id,
          this.accountId,
          this.metricsFrom || undefined,
          this.metricsTo || undefined
        );
        this.metrics = data;
      } catch {
        useAlert('Erro ao carregar relatório.');
        this.metricsModal = false;
      } finally {
        this.metricsLoading = false;
      }
    },
    async reloadMetrics() {
      if (!this.current) return;
      this.metricsLoading = true;
      this.metrics = null;
      try {
        const { data } = await pipelineAPI.getMetrics(
          this.current.id,
          this.accountId,
          this.metricsFrom || undefined,
          this.metricsTo || undefined
        );
        this.metrics = data;
      } catch {
        useAlert('Erro ao carregar relatório.');
      } finally {
        this.metricsLoading = false;
      }
    },
    async downloadCsv() {
      if (!this.current) return;
      this.metricsExporting = true;
      try {
        const { data } = await pipelineAPI.exportCsv(this.current.id, this.accountId);
        const url = URL.createObjectURL(new Blob([data], { type: 'text/csv;charset=utf-8;' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `pipeline_${this.current.id}_export.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch {
        useAlert('Erro ao exportar CSV.');
      } finally {
        this.metricsExporting = false;
      }
    },
    metricsMaxStageCount() {
      if (!this.metrics?.byStage?.length) return 1;
      return Math.max(...this.metrics.byStage.map(s => s.cardCount), 1);
    },
    metricsMaxMonthCount() {
      if (!this.metrics?.createdByMonth?.length) return 1;
      return Math.max(...this.metrics.createdByMonth.map(m => m.count), 1);
    },
    fmtMonth(yyyyMM) {
      const [y, m] = yyyyMM.split('-');
      const names = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
      return `${names[parseInt(m, 10) - 1]}/${y.slice(2)}`;
    },

    // ---- Settings do pipeline ----
    async openSettings() {
      if (!this.current) return;
      await this.$store.dispatch('inboxes/get');
      await this.loadWebhooks();
      try {
        const { data } = await pipelineAPI.getSettings(this.current.id, this.accountId);
        this.settingsForm = {
          automations: {
            autoCreateCard: false,
            autoAssignAgent: false,
            autoMoveOnResolve: false,
            autoMoveOnReopen: false,
            notifyAgentOnAssign: false,
            notifyOnCardOverdue: false,
            notifyOnStageChange: false,
            showClosedConversations: false,
            ...(data.automations || {}),
          },
          inboxIds: data.inboxIds || [],
        };
      } catch {
        this.settingsForm = {
          automations: {
            autoCreateCard: false, autoAssignAgent: false, autoMoveOnResolve: false,
            autoMoveOnReopen: false, notifyAgentOnAssign: false, notifyOnCardOverdue: false,
            notifyOnStageChange: false, showClosedConversations: false,
          },
          inboxIds: [],
        };
      }
      this.settingsModal = true;
    },
    toggleSettingsInbox(inboxId) {
      const ids = this.settingsForm.inboxIds;
      const idx = ids.indexOf(inboxId);
      if (idx === -1) this.settingsForm.inboxIds = [...ids, inboxId];
      else this.settingsForm.inboxIds = ids.filter(id => id !== inboxId);
    },
    async saveSettings() {
      try {
        await pipelineAPI.updateSettings(this.current.id, this.settingsForm, this.accountId);
        this.settingsModal = false;
        useAlert('Configurações salvas.');
      } catch {
        useAlert('Erro ao salvar configurações.');
      }
    },

    // ---- Accordion sections ----
    toggleSection(key) {
      this.sectionOpen = { ...this.sectionOpen, [key]: !this.sectionOpen[key] };
    },

    // ---- Labels ----
    toggleCardLabel(title) {
      const current = this.cardForm.selectedLabels || [];
      if (current.includes(title)) {
        this.cardForm.selectedLabels = current.filter(l => l !== title);
      } else {
        this.cardForm.selectedLabels = [...current, title];
      }
    },

    // ---- Produtos do catálogo ----
    async loadCatalogProducts() {
      if (!this.current) return;
      try {
        const { data } = await pipelineAPI.getProducts(this.current.id, this.accountId, false);
        this.catalogProducts = data;
      } catch (e) {
        console.error('[loadCatalogProducts]', e);
        this.catalogProducts = [];
      }
    },
    async loadCardProducts(cardId) {
      try {
        const { data } = await pipelineAPI.getCardProducts(cardId, this.accountId);
        this.cardForm.selectedProducts = data.map(p => ({ ...p }));
        this.cardForm.originalProductItemIds = data.map(p => p.id);
      } catch (e) {
        console.error('[loadCardProducts]', e);
        this.cardForm.selectedProducts = [];
        this.cardForm.originalProductItemIds = [];
      }
    },
    addProductLine() {
      if (!this.productPickId) return;
      const product = this.catalogProducts.find(p => p.id === this.productPickId);
      if (!product) return;
      const qty = parseFloat(this.productPickQty) || 1;
      const disc = parseFloat(this.productPickDiscount) || 0;
      this.cardForm.selectedProducts = [
        ...(this.cardForm.selectedProducts || []),
        {
          productId: product.id,
          productName: product.name,
          quantity: qty,
          unitPrice: parseFloat(product.unitPrice),
          discountPercentage: disc,
        },
      ];
      this.productPickId = null;
      this.productPickQty = 1;
      this.productPickDiscount = 0;
    },
    removeProductLine(index) {
      this.cardForm.selectedProducts = (this.cardForm.selectedProducts || []).filter((_, i) => i !== index);
    },
    productLineSubtotal(item) {
      const qty = parseFloat(item.quantity) || 0;
      const price = parseFloat(item.unitPrice) || 0;
      const disc = parseFloat(item.discountPercentage) || 0;
      return qty * price * (1 - disc / 100);
    },
    async syncCardProducts(cardId) {
      for (const itemId of (this.cardForm.originalProductItemIds || [])) {
        try { await pipelineAPI.deleteCardProduct(cardId, itemId, this.accountId); } catch {}
      }
      for (const item of (this.cardForm.selectedProducts || [])) {
        try {
          await pipelineAPI.addCardProduct(cardId, {
            productId: item.productId,
            quantity: item.quantity,
            unitPrice: item.unitPrice,
            discountPercentage: item.discountPercentage,
          }, this.accountId);
        } catch (e) {
          console.error('[syncCardProducts add]', e);
        }
      }
    },

    hideAssigneeDropdown() {
      this._hideAgentTimer = setTimeout(() => { this.showAgentDropdown = false; }, 150);
    },
    hideParticipantDropdown() {
      this._hideParticipantTimer = setTimeout(() => { this.showParticipantDropdown = false; }, 150);
    },

    // ---- Won/Lost ----------------------------------------------------------
    async closeCard(outcome) {
      if (!this.cardForm.id) return;
      try {
        const resp = await pipelineAPI.closeCard(this.cardForm.id, outcome, this.accountId, this.userId);
        this.cardForm.status = resp.data.status;
        const msg = outcome === 'won' ? 'Card marcado como Ganho!' :
                    outcome === 'lost' ? 'Card marcado como Perdido!' : 'Card reaberto!';
        useAlert(msg);
        await this.applyFilters();
        await this.fetchOverview();
      } catch {
        useAlert('Erro ao atualizar status do card.');
      }
    },

    // ---- Webhooks (configuração de n8n/integrações) -------------------------
    async loadWebhooks() {
      try {
        const { data } = await pipelineAPI.getWebhooks(this.accountId);
        this.webhooks = data;
      } catch { this.webhooks = []; }
    },
    async addWebhook() {
      if (!this.webhookNewUrl.trim()) return;
      try {
        await pipelineAPI.createWebhook({
          url: this.webhookNewUrl.trim(),
          description: this.webhookNewDesc.trim(),
          events: [...this.webhookNewEvents],
        }, this.accountId);
        this.webhookNewUrl = '';
        this.webhookNewDesc = '';
        await this.loadWebhooks();
        useAlert('Webhook cadastrado.');
      } catch { useAlert('Erro ao cadastrar webhook.'); }
    },
    async removeWebhook(id) {
      try {
        await pipelineAPI.deleteWebhook(id, this.accountId);
        await this.loadWebhooks();
        useAlert('Webhook removido.');
      } catch { useAlert('Erro ao remover webhook.'); }
    },
  },
};
</script>

<template>
  <section class="flex w-full h-full overflow-hidden bg-n-surface-1">

    <!-- ── Sidebar retrátil: lista de funis ── -->
    <aside
      class="relative flex flex-col flex-shrink-0 border-r border-n-weak bg-n-solid-1 overflow-hidden transition-all duration-200 ease-in-out"
      :style="{ width: sidebarOpen ? '240px' : '40px' }"
    >
      <!-- Cabeçalho da sidebar (sempre visível) -->
      <div class="flex items-center gap-1.5 px-2 py-2.5 border-b border-n-weak flex-shrink-0">
        <span v-if="sidebarOpen" class="flex-1 text-sm font-semibold text-n-slate-12 truncate">Funis</span>
        <button
          :title="sidebarOpen ? 'Recolher painel' : 'Expandir painel'"
          class="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded text-n-slate-11 cursor-pointer transition-colors hover:bg-n-alpha-1 active:bg-n-alpha-2 text-sm font-bold"
          @click="sidebarOpen = !sidebarOpen"
        >{{ sidebarOpen ? '‹' : '›' }}</button>
      </div>

      <!-- Conteúdo — visível apenas expandido -->
      <div v-show="sidebarOpen" class="flex-1 flex flex-col p-2 overflow-auto min-w-0">
        <button
          class="w-full mb-2 px-2 py-1.5 text-xs text-white rounded-md bg-n-solid-blue cursor-pointer select-none transition-all hover:brightness-110 active:brightness-90 active:scale-95"
          @click="openPipelineModal"
        >+ Novo Funil</button>
        <button
          v-for="p in pipelines"
          :key="p.id"
          class="w-full p-2 mb-1 text-left rounded-lg border border-n-weak cursor-pointer select-none transition-colors hover:bg-n-alpha-1 active:bg-n-alpha-2"
          :class="{ 'border-n-brand bg-n-alpha-1': current && current.id === p.id }"
          @click="openPipeline(p.id)"
        >
          <div class="flex items-center justify-between gap-1 text-sm text-n-slate-12">
            <span class="font-medium truncate">{{ p.name }}<span v-if="p.isFavorite"> ★</span></span>
            <span class="flex-shrink-0 text-[10px] text-n-slate-10">#{{ p.id }}</span>
          </div>
          <div class="mt-0.5 text-xs text-n-slate-11">
            {{ p.tasksCount }} cards · {{ money(p.aggregateValue, p.currency) }}
          </div>
        </button>
        <p v-if="!pipelines.length && !loading" class="mt-4 text-xs text-center text-n-slate-10">
          Nenhum funil.<br />Crie um para começar.
        </p>
      </div>
    </aside>

    <!-- ── Área do board ── -->
    <div class="flex-1 flex flex-col overflow-hidden">

      <!-- Cabeçalho do board -->
      <div v-if="current" class="relative flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 border-b border-n-weak bg-n-solid-1 flex-shrink-0 flex-wrap sm:flex-nowrap">
        <!-- Botão abrir sidebar (visível apenas quando fechada em mobile) -->
        <button
          v-if="!sidebarOpen"
          class="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded border border-n-weak text-n-slate-11 cursor-pointer hover:bg-n-alpha-1 sm:hidden"
          title="Funis"
          @click="sidebarOpen = true"
        >≡</button>
        <!-- Nome + badges de stats -->
        <h2 class="text-sm sm:text-base font-semibold text-n-slate-12 flex-shrink-0 truncate max-w-[110px] sm:max-w-[140px]">{{ current.name }}</h2>
        <span class="px-1.5 py-0.5 text-xs rounded-full bg-n-alpha-2 text-n-slate-11 flex-shrink-0">{{ current.tasksCount }}</span>
        <span class="px-2 py-0.5 text-xs text-green-700 rounded-full bg-green-100 flex-shrink-0 hidden sm:inline-flex">{{ money(current.aggregateValue, current.currency) }}</span>
        <span class="px-2 py-0.5 text-xs rounded-full bg-n-alpha-2 text-n-slate-11 flex-shrink-0 hidden xl:inline-flex">ponderado: {{ money(current.weightedValue, current.currency) }}</span>

        <!-- Filtros — sempre visíveis, ocupam o espaço disponível -->
        <div class="flex items-center gap-1.5 overflow-hidden" style="flex: 1 1 0; min-width: 0;">
          <input
            v-model="filters.q"
            type="text"
            placeholder="🔍 Buscar..."
            class="py-1 px-2 text-xs rounded border border-n-weak bg-n-surface-1 text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:border-n-brand transition-colors"
            style="flex: 1 1 0; min-width: 60px; max-width: 180px;"
            @input="onFilterQChange"
          />
          <select
            v-model="situacaoCards"
            class="py-1 px-1 text-xs rounded border border-n-weak bg-n-surface-1 text-n-slate-11 focus:outline-none focus:border-n-brand cursor-pointer transition-colors flex-shrink-0"
            style="width: 92px;"
            :class="{ 'border-n-brand text-n-brand font-semibold': situacaoCards !== 'abertos' }"
            title="Quais cards mostrar: abertos, fechados (ganhos e perdidos) ou todos"
            @change="applyFilters"
          >
            <option value="abertos">Abertos</option>
            <option value="fechados">Fechados</option>
            <option value="todos">Todos</option>
          </select>
          <select
            v-model="filters.priority"
            class="py-1 px-1 text-xs rounded border border-n-weak bg-n-surface-1 text-n-slate-11 focus:outline-none focus:border-n-brand cursor-pointer transition-colors flex-shrink-0 hidden sm:block"
            style="width: 84px;"
            :class="{ 'border-n-brand text-n-brand font-semibold': filters.priority }"
            @change="applyFilters"
          >
            <option value="">Prioridade</option>
            <option value="urgent">🔴 Urgente</option>
            <option value="high">🟠 Alta</option>
            <option value="medium">🟡 Média</option>
            <option value="low">🟢 Baixa</option>
          </select>
          <select
            v-model="filters.assigneeId"
            class="py-1 px-1 text-xs rounded border border-n-weak bg-n-surface-1 text-n-slate-11 focus:outline-none focus:border-n-brand cursor-pointer transition-colors flex-shrink-0 hidden sm:block"
            style="width: 84px;"
            :class="{ 'border-n-brand text-n-brand font-semibold': filters.assigneeId }"
            @change="applyFilters"
          >
            <option :value="null">Agente</option>
            <option v-for="a in agents" :key="a.id" :value="a.id">{{ a.name }}</option>
          </select>
          <button
            v-if="hasFilters"
            class="flex items-center gap-1 px-1.5 py-1 text-xs rounded border border-red-200 text-red-500 hover:bg-red-50 transition-colors cursor-pointer flex-shrink-0"
            title="Limpar filtros"
            @click="clearFilters"
          >
            ✕<span class="w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center leading-none">{{ activeFiltersCount }}</span>
          </button>
        </div>

        <!-- Botão "···" com menu de ações -->
        <div class="relative flex-shrink-0">
          <button
            class="flex items-center justify-center w-8 h-7 rounded border border-n-weak text-n-slate-11 text-base font-bold cursor-pointer hover:bg-n-alpha-1 transition-colors select-none"
            :class="{ 'bg-n-alpha-2 border-n-brand': moreMenuOpen }"
            title="Ações"
            @click="moreMenuOpen = !moreMenuOpen"
          >···</button>

          <!-- Dropdown de ações -->
          <div
            v-if="moreMenuOpen"
            class="absolute top-full right-0 mt-1 z-50 w-44 rounded-lg border border-n-weak bg-n-solid-1 shadow-lg py-1 overflow-hidden"
            @click.stop
          >
            <button
              class="w-full px-3 py-2 text-xs text-left text-n-slate-12 hover:bg-n-alpha-1 transition-colors cursor-pointer flex items-center gap-2"
              @click="toggleFav(current.id); moreMenuOpen = false"
            >
              <span>{{ current.isFavorite ? '★' : '☆' }}</span>
              {{ current.isFavorite ? 'Remover favorito' : 'Favoritar' }}
            </button>
            <div class="h-px bg-n-weak mx-2 my-1" />
            <button
              class="w-full px-3 py-2 text-xs text-left text-n-slate-12 hover:bg-n-alpha-1 transition-colors cursor-pointer flex items-center gap-2"
              @click="openMetrics(); moreMenuOpen = false"
            >
              <span>📊</span> Relatórios
            </button>
            <button
              class="w-full px-3 py-2 text-xs text-left text-n-slate-12 hover:bg-n-alpha-1 transition-colors cursor-pointer flex items-center gap-2"
              @click="openSettings(); moreMenuOpen = false"
            >
              <span>⚙</span> Configurações
            </button>
            <button
              class="w-full px-3 py-2 text-xs text-left text-n-slate-12 hover:bg-n-alpha-1 transition-colors cursor-pointer flex items-center gap-2"
              @click="openStageModal(); moreMenuOpen = false"
            >
              <span class="font-bold text-n-brand">+</span> Nova etapa
            </button>
            <button
              class="w-full px-3 py-2 text-xs text-left text-n-slate-12 hover:bg-n-alpha-1 transition-colors cursor-pointer flex items-center gap-2"
              @click="openCardModal(null); moreMenuOpen = false"
            >
              <span class="font-bold text-n-brand">+</span> Novo card
            </button>
          </div>

          <!-- Backdrop para fechar ao clicar fora -->
          <div v-if="moreMenuOpen" class="fixed inset-0 z-40" @click="moreMenuOpen = false" />
        </div>
      </div>

      <!-- Estado vazio -->
      <div v-if="!current" class="flex-1 flex items-center justify-center">
        <p class="text-sm text-n-slate-10">
          {{ sidebarOpen ? 'Selecione um funil à esquerda.' : 'Abra o painel lateral e selecione um funil.' }}
        </p>
      </div>

      <!-- Colunas de etapas -->
      <div v-else class="flex-1 overflow-x-auto overflow-y-hidden p-2 sm:p-4">
        <div class="flex items-start gap-2 sm:gap-4 h-full min-w-max sm:min-w-0">
          <div
            v-for="st in current.stages"
            :key="st.id"
            class="flex-shrink-0 w-72 sm:w-80 flex flex-col rounded-xl border border-n-weak bg-n-solid-1 overflow-hidden"
            :style="{
              borderTopColor: st.color || '#670961',
              borderTopWidth: '3px',
              maxHeight: 'calc(100vh - 160px)',
            }"
          >
            <!-- Cabeçalho da etapa colorido -->
            <div
              class="flex items-center gap-2 px-3 py-2.5 border-b border-n-weak flex-shrink-0"
              :style="{ backgroundColor: (st.color || '#670961') + '28' }"
            >
              <span class="w-3 h-3 rounded-full flex-shrink-0" :style="{ background: st.color }" />
              <span class="text-sm font-semibold text-n-slate-12 flex-1 truncate">{{ st.name }}</span>
              <span
                class="px-1.5 py-0.5 text-[10px] font-medium rounded-full text-n-slate-12"
                :class="hasFilters || situacaoCards !== 'abertos' ? 'bg-n-brand/20 text-n-brand' : 'bg-white/60'"
                :title="tituloDoContador(st)"
              >{{ contadorDaEtapa(st) }}</span>
              <span
                v-if="naoLidasPorEtapa[st.id]"
                class="inline-flex items-center justify-center flex-shrink-0 h-5 min-w-5 px-1.5 rounded-full bg-n-ruby-9 text-white text-[11px] font-semibold leading-none"
                :title="`${naoLidasPorEtapa[st.id]} mensagens não lidas nesta etapa`"
              >{{ textoNaoLidas(naoLidasPorEtapa[st.id]) }}</span>
              <span v-if="st.isWon" class="text-xs text-green-700 font-bold" title="Etapa Ganha">✔</span>
              <span v-if="st.isLost" class="text-xs text-red-700 font-bold" title="Etapa Perdida">✕</span>
              <button
                title="Editar etapa"
                class="p-1 rounded text-n-slate-11 cursor-pointer transition-colors hover:bg-white/40 active:bg-white/60"
                @click="openStageModal(st)"
              >✎</button>
              <button
                title="Novo card"
                class="p-1 rounded text-n-slate-11 cursor-pointer transition-colors hover:bg-white/40 active:bg-white/60 font-bold"
                @click="openCardModal(st.id)"
              >+</button>
            </div>
            <!-- Valor agregado da etapa -->
            <div v-if="st.aggregateValue" class="px-3 py-1 text-xs text-n-slate-10 border-b border-n-weak/50 bg-n-alpha-1 flex-shrink-0">
              {{ money(st.aggregateValue, current.currency) }}
            </div>
            <!-- Scroll de cards -->
            <div class="flex-1 overflow-y-auto p-3">
              <draggable
                :list="cards[st.id] || []"
                group="kanban-cards"
                item-key="id"
                animation="150"
                ghost-class="kanban-ghost"
                drag-class="kanban-dragging"
                class="space-y-3 min-h-[48px]"
                @start="onCardDragStart(st.id)"
                @add="ev => onCardDropIntoStage(st.id, ev)"
              >
              <template #item="{ element: c }">
              <div
                :class="[
                  'p-3 rounded-lg border border-n-weak bg-n-surface-1 cursor-grab active:cursor-grabbing transition-all hover:border-n-brand hover:shadow-md',
                  isCardOverdue(c) ? 'border-l-4 border-l-red-500' : ''
                ]"
                title="Arraste para mover ou clique para editar"
                @click="openCardModal(st.id, c)"
              >
                <!-- Topo: Agente (esquerda) | Conversas (direita) -->
                <div
                  v-if="(c.assigneeIds || []).length || (c.conversationIds || []).length"
                  class="flex items-center justify-between gap-2 mb-2.5 pb-2 border-b border-n-weak/40"
                  @click.stop
                >
                  <!-- Agente responsável principal + nome -->
                  <div v-if="(c.assigneeIds || []).length" class="flex items-center gap-1.5 min-w-0 flex-1">
                    <span
                      class="flex-shrink-0 flex items-center justify-center w-5 h-5 text-[9px] font-bold text-white rounded-full bg-n-solid-blue"
                      :title="agents.find(a => a.id === c.assigneeIds[0])?.name"
                    >{{ agentInitials(agents.find(a => a.id === c.assigneeIds[0])) }}</span>
                    <span class="text-xs font-medium text-n-slate-12 truncate">
                      {{ agents.find(a => a.id === c.assigneeIds[0])?.name || `#${c.assigneeIds[0]}` }}
                    </span>
                    <span v-if="c.assigneeIds.length > 1" class="flex-shrink-0 text-[10px] text-n-slate-10">+{{ c.assigneeIds.length - 1 }}</span>
                  </div>
                  <span v-else class="flex-1" />
                  <!-- Conversas vinculadas -->
                  <div v-if="(c.conversationIds || []).length" class="flex flex-wrap justify-end gap-1 flex-shrink-0">
                    <button
                      v-for="convId in c.conversationIds"
                      :key="convId"
                      :title="tituloDaConversa(convId)"
                      class="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded-md transition-colors cursor-pointer"
                      :class="naoLidasDaConversa(convId) ? 'bg-n-ruby-9/10 text-n-ruby-11 ring-1 ring-n-ruby-9/40 hover:bg-n-ruby-9/20' : 'bg-n-alpha-2 text-n-brand hover:bg-n-alpha-3'"
                      @click="goToConversation(convId)"
                    >💬 #{{ convId }}<span
                      v-if="naoLidasDaConversa(convId)"
                      class="inline-flex items-center justify-center h-4 min-w-4 px-1 rounded-full bg-n-ruby-9 text-white text-[10px] font-semibold leading-none"
                    >{{ textoNaoLidas(naoLidasDaConversa(convId)) }}</span></button>
                  </div>
                </div>

                <!-- Título -->
                <div class="text-sm font-semibold text-n-slate-12 leading-snug">{{ c.title }}</div>
                <!-- Descrição (até 4 linhas, renderizada como markdown) -->
                <div
                  v-if="c.description"
                  class="mt-1.5 text-xs text-n-slate-11 line-clamp-4 leading-relaxed prose prose-xs max-w-none [&_strong]:font-semibold [&_em]:italic [&_ul]:list-disc [&_ul]:pl-3 [&_ol]:list-decimal [&_ol]:pl-3"
                  v-dompurify-html="formatMessage(c.description, false, false)"
                />
                <!-- Badge de vencimento -->
                <div v-if="c.dueDate" class="mt-2">
                  <span
                    class="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-md border"
                    :class="dueDateClass(c.dueDate)"
                    :title="dueDateTitle(c.dueDate)"
                  >🗓 {{ dueDateLabel(c.dueDate) }}</span>
                </div>
                <!-- Status Won/Lost badge -->
                <div v-if="c.status === 'WON' || c.status === 'LOST'" class="mt-2">
                  <span
                    class="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold text-white rounded-md"
                    :class="c.status === 'WON' ? 'bg-green-600' : 'bg-red-600'"
                  >{{ c.status === 'WON' ? '✔ GANHO' : '✕ PERDIDO' }}</span>
                </div>
                <!-- Prioridade, valor, etiquetas -->
                <div class="flex flex-wrap items-center gap-1.5 mt-2">
                  <span
                    v-if="c.priority"
                    class="px-2 py-0.5 text-[10px] font-medium text-white rounded-md"
                    :class="{
                      'bg-red-600': c.priority === 'urgent',
                      'bg-orange-500': c.priority === 'high',
                      'bg-yellow-500': c.priority === 'medium',
                      'bg-green-600': c.priority === 'low',
                    }"
                  >{{ c.priority }}</span>
                  <span v-if="c.value" class="text-xs font-medium text-n-slate-11">{{ money(c.value, current.currency) }}</span>
                  <span
                    v-for="l in c.labels"
                    :key="l"
                    class="px-1.5 py-0.5 text-[10px] rounded-full text-white font-medium"
                    :style="{ background: (accountLabels.find(al => al.title === l) || {}).color || '#64748b' }"
                  >{{ l }}</span>
                </div>

                <!-- Barra de ações: mover (esquerda) | editar/histórico/excluir (direita) -->
                <div class="flex items-center justify-between mt-2 pt-2 border-t border-n-weak/50" @click.stop>
                  <!-- Esquerda: navegar entre etapas -->
                  <div class="flex items-center gap-1">
                    <button
                      title="Mover para etapa anterior"
                      class="px-2 py-0.5 text-xs rounded border border-n-weak text-n-slate-11 cursor-pointer select-none transition-colors hover:bg-n-alpha-1 active:bg-n-alpha-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                      :disabled="!canMove(st, -1)"
                      @click="move(c, -1)"
                    >◀</button>
                    <button
                      title="Mover para próxima etapa"
                      class="px-2 py-0.5 text-xs rounded border border-n-weak text-n-slate-11 cursor-pointer select-none transition-colors hover:bg-n-alpha-1 active:bg-n-alpha-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                      :disabled="!canMove(st, 1)"
                      @click="move(c, 1)"
                    >▶</button>
                  </div>
                  <!-- Direita: transferir, editar, histórico, excluir -->
                  <div class="flex items-center gap-1">
                    <button
                      title="Transferir para outro funil"
                      class="px-2 py-0.5 text-xs rounded border border-n-weak text-n-slate-11 cursor-pointer select-none transition-colors hover:bg-n-alpha-1 active:bg-n-alpha-2"
                      @click="openTransfer(c)"
                    >📤</button>
                    <button
                      title="Editar card"
                      class="px-2 py-0.5 text-xs rounded border border-n-weak text-n-slate-11 cursor-pointer select-none transition-colors hover:bg-n-alpha-1 active:bg-n-alpha-2"
                      @click="openCardModal(st.id, c)"
                    >✎</button>
                    <button
                      title="Ver histórico"
                      class="px-2 py-0.5 text-xs rounded border border-n-weak text-n-slate-11 cursor-pointer select-none transition-colors hover:bg-n-alpha-1 active:bg-n-alpha-2"
                      @click="showTimeline(c)"
                    >🕓</button>
                    <button
                      title="Excluir card"
                      class="px-2 py-0.5 text-xs rounded border border-n-brand text-n-brand cursor-pointer select-none transition-colors hover:bg-n-alpha-1 active:bg-n-alpha-2"
                      @click="delCard(c)"
                    >🗑</button>
                  </div>
                </div>
              </div>
              </template>
              </draggable>
            </div>
          </div>
          <p v-if="!current.stages.length" class="text-sm text-n-slate-10 mt-8">
            Sem etapas. Clique em "+ Etapa".
          </p>
        </div>
      </div>
    </div>

    <!-- Modal: Relatórios do Pipeline -->
    <div v-if="metricsModal" class="fixed inset-0 z-[60] flex items-center justify-center" style="background: rgba(55, 65, 81, 0.80)" @click.self="metricsModal = false">
      <div class="w-[52rem] rounded-xl bg-n-solid-1 border border-n-weak max-h-[90vh] flex flex-col">

        <!-- Header -->
        <div class="px-5 pt-4 pb-3 border-b border-n-weak flex-shrink-0">
          <div class="flex items-center justify-between gap-3 mb-2">
            <div>
              <h3 class="text-base font-semibold text-n-slate-12">📊 Relatórios</h3>
              <p v-if="metrics" class="text-xs text-n-slate-10 mt-0.5">{{ metrics.pipelineName }}</p>
            </div>
            <button class="text-n-slate-9 hover:text-n-slate-12 text-lg cursor-pointer flex-shrink-0" @click="metricsModal = false">✕</button>
          </div>
          <!-- Filtros de período -->
          <div class="flex flex-wrap items-center gap-2">
            <span class="text-xs text-n-slate-9">Período:</span>
            <input
              v-model="metricsFrom"
              type="date"
              class="!mb-0 text-xs py-1 px-2 w-36"
              placeholder="De"
              @change="reloadMetrics"
            />
            <span class="text-xs text-n-slate-9">até</span>
            <input
              v-model="metricsTo"
              type="date"
              class="!mb-0 text-xs py-1 px-2 w-36"
              placeholder="Até"
              @change="reloadMetrics"
            />
            <button
              v-if="metricsFrom || metricsTo"
              class="text-xs text-red-500 hover:text-red-700 cursor-pointer"
              @click="metricsFrom = ''; metricsTo = ''; reloadMetrics()"
            >✕ limpar</button>
          </div>
        </div>

        <!-- Loading -->
        <div v-if="metricsLoading" class="flex-1 flex items-center justify-center py-16 text-n-slate-9 text-sm gap-2">
          <div class="w-4 h-4 border-2 border-n-brand border-t-transparent rounded-full animate-spin" />
          Carregando...
        </div>

        <!-- Conteúdo -->
        <div v-else-if="metrics" class="flex-1 overflow-y-auto p-5 space-y-6">

          <!-- KPIs principais -->
          <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div class="rounded-lg border border-n-weak bg-n-surface-1 p-3 text-center">
              <div class="text-2xl font-bold text-n-slate-12">{{ metrics.total }}</div>
              <div class="text-[10px] uppercase tracking-wide text-n-slate-9 mt-0.5">Total</div>
            </div>
            <div class="rounded-lg border border-green-200 bg-green-50 p-3 text-center">
              <div class="text-2xl font-bold text-green-700">{{ metrics.won }}</div>
              <div class="text-[10px] uppercase tracking-wide text-green-600 mt-0.5">Ganhos</div>
            </div>
            <div class="rounded-lg border border-red-200 bg-red-50 p-3 text-center">
              <div class="text-2xl font-bold text-red-700">{{ metrics.lost }}</div>
              <div class="text-[10px] uppercase tracking-wide text-red-600 mt-0.5">Perdidos</div>
            </div>
            <div class="rounded-lg border border-orange-200 bg-orange-50 p-3 text-center">
              <div class="text-2xl font-bold text-orange-600">{{ metrics.overdue }}</div>
              <div class="text-[10px] uppercase tracking-wide text-orange-500 mt-0.5">Vencidos</div>
            </div>
          </div>

          <!-- Taxa de conversão + tempo médio -->
          <div class="grid grid-cols-3 gap-3">
            <div class="rounded-lg border border-n-weak bg-n-surface-1 p-3">
              <div class="text-xs text-n-slate-9 mb-1">Taxa de conversão</div>
              <div class="flex items-end gap-1">
                <span class="text-xl font-bold text-n-slate-12">{{ metrics.conversionRate }}%</span>
                <span class="text-[10px] text-n-slate-9 mb-0.5">ganhos/(ganhos+perdidos)</span>
              </div>
              <div class="mt-2 h-2 rounded-full bg-n-alpha-2 overflow-hidden">
                <div class="h-full rounded-full bg-green-500 transition-all" :style="{ width: metrics.conversionRate + '%' }" />
              </div>
            </div>
            <div class="rounded-lg border border-n-weak bg-n-surface-1 p-3">
              <div class="text-xs text-n-slate-9 mb-1">Tempo médio para fechar</div>
              <div class="text-xl font-bold text-n-slate-12">{{ metrics.avgDaysToClose }} <span class="text-sm font-normal text-n-slate-9">dias</span></div>
              <div class="text-[10px] text-n-slate-9 mt-1">Média dos cards Ganhos</div>
            </div>
            <div class="rounded-lg border border-n-weak bg-n-surface-1 p-3">
              <div class="text-xs text-n-slate-9 mb-1">Valor em aberto</div>
              <div class="text-xl font-bold text-n-slate-12 truncate">{{ money(metrics.openValue, current?.currency) }}</div>
              <div class="flex gap-2 mt-1">
                <span class="text-[10px] text-green-600">✔ {{ money(metrics.wonValue, current?.currency) }}</span>
                <span class="text-[10px] text-red-500">✕ {{ money(metrics.lostValue, current?.currency) }}</span>
              </div>
            </div>
          </div>

          <!-- Funil por etapa -->
          <div>
            <h4 class="text-xs font-bold uppercase tracking-wide text-n-slate-9 mb-3">Funil por Etapa</h4>
            <div class="space-y-2">
              <div v-for="stage in metrics.byStage" :key="stage.stageId" class="flex items-center gap-3">
                <div class="w-28 text-xs text-n-slate-11 truncate flex-shrink-0 text-right">{{ stage.stageName }}</div>
                <div class="flex-1 h-6 bg-n-alpha-1 rounded-md overflow-hidden relative">
                  <div
                    class="h-full rounded-md transition-all flex items-center px-2"
                    :style="{ width: (stage.cardCount / metricsMaxStageCount() * 100) + '%', background: stage.color || '#670961' }"
                  >
                    <span v-if="stage.cardCount > 0" class="text-[10px] font-bold text-white">{{ stage.cardCount }}</span>
                  </div>
                  <span v-if="stage.cardCount === 0" class="absolute inset-0 flex items-center px-2 text-[10px] text-n-slate-9">0</span>
                </div>
                <div class="w-20 text-right text-[10px] text-n-slate-9 flex-shrink-0">
                  <span v-if="stage.totalValue > 0">{{ money(stage.totalValue, current?.currency) }}</span>
                  <span v-else class="text-n-slate-7">—</span>
                </div>
                <div class="w-16 text-right text-[10px] text-n-slate-9 flex-shrink-0">
                  <span v-if="stage.cardCount > 0">{{ stage.avgDaysInStage }}d</span>
                </div>
              </div>
              <div class="flex items-center gap-3 pt-1 border-t border-n-weak">
                <div class="w-28" />
                <div class="flex-1 text-[10px] text-n-slate-9">barras proporcionais ao nº de cards</div>
                <div class="w-20 text-right text-[10px] text-n-slate-9">valor total</div>
                <div class="w-16 text-right text-[10px] text-n-slate-9">dias na etapa</div>
              </div>
            </div>
          </div>

          <!-- Cards criados por mês -->
          <div>
            <h4 class="text-xs font-bold uppercase tracking-wide text-n-slate-9 mb-3">Cards Criados (últimos 6 meses)</h4>
            <div class="flex items-end gap-2 h-24">
              <div
                v-for="m in metrics.createdByMonth"
                :key="m.month"
                class="flex-1 flex flex-col items-center gap-1"
              >
                <span class="text-[10px] font-medium text-n-slate-11">{{ m.count || '' }}</span>
                <div class="w-full rounded-t-md bg-n-brand/80 transition-all" :style="{ height: (m.count / metricsMaxMonthCount() * 64) + 'px', minHeight: m.count > 0 ? '4px' : '0' }" />
                <span class="text-[9px] text-n-slate-9">{{ fmtMonth(m.month) }}</span>
              </div>
            </div>
          </div>

        </div>

        <!-- Footer -->
        <div class="px-5 py-3 border-t border-n-weak flex-shrink-0 flex items-center justify-between gap-2">
          <button
            class="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded border border-n-weak text-n-slate-11 hover:bg-n-alpha-1 cursor-pointer disabled:opacity-50"
            :disabled="metricsExporting"
            @click="downloadCsv"
          >
            <span v-if="metricsExporting" class="w-3 h-3 border border-n-slate-9 border-t-transparent rounded-full animate-spin inline-block" />
            <span v-else>⬇</span>
            Exportar CSV
          </button>
          <button class="px-4 py-1.5 text-sm rounded border border-n-weak text-n-slate-11 hover:bg-n-alpha-1 cursor-pointer" @click="metricsModal = false">Fechar</button>
        </div>
      </div>
    </div>

    <!-- Modal: Configurações do Pipeline -->
    <div v-if="settingsModal" class="fixed inset-0 z-[60] flex items-center justify-center" style="background: rgba(55, 65, 81, 0.80)" @click.self="settingsModal = false">
      <div class="w-[38rem] rounded-xl bg-n-solid-1 border border-n-weak max-h-[88vh] flex flex-col">

        <!-- Header -->
        <div class="px-5 pt-4 pb-3 border-b border-n-weak flex-shrink-0 flex items-center justify-between">
          <div>
            <h3 class="text-base font-semibold text-n-slate-12">Configurações do Pipeline</h3>
            <p class="mt-0.5 text-xs text-n-slate-11">{{ current?.name }}</p>
          </div>
          <button class="w-7 h-7 flex items-center justify-center rounded text-n-slate-11 text-lg cursor-pointer hover:bg-n-alpha-1" @click="settingsModal = false">✕</button>
        </div>

        <div class="flex-1 overflow-y-auto px-5 py-4 space-y-6">

          <!-- Inboxes vinculadas -->
          <div>
            <p class="text-xs font-semibold uppercase tracking-wide text-n-slate-10 mb-2">Inboxes vinculadas</p>
            <p class="text-xs text-n-slate-11 mb-3">Conversas recebidas nestas inboxes poderão gerar cards automaticamente neste pipeline.</p>
            <div v-if="inboxes.length" class="space-y-1.5">
              <label
                v-for="inbox in inboxes"
                :key="inbox.id"
                class="flex items-center gap-3 px-3 py-2 rounded-lg border cursor-pointer transition-colors select-none"
                :class="settingsForm.inboxIds.includes(inbox.id) ? 'border-n-brand bg-n-alpha-1' : 'border-n-weak hover:bg-n-alpha-1/50'"
                @click="toggleSettingsInbox(inbox.id)"
              >
                <span
                  class="w-4 h-4 flex-shrink-0 rounded flex items-center justify-center border text-[10px] font-bold text-white transition-colors"
                  :style="settingsForm.inboxIds.includes(inbox.id) ? 'background:#3B82F6;border-color:#3B82F6' : 'border-color:#ccc'"
                >{{ settingsForm.inboxIds.includes(inbox.id) ? '✓' : '' }}</span>
                <span class="flex-1 text-sm text-n-slate-12">{{ inbox.name }}</span>
                <span class="text-[10px] text-n-slate-10 uppercase">{{ inbox.channelType?.replace('Channel::', '') }}</span>
              </label>
            </div>
            <p v-else class="text-xs text-n-slate-10 py-2">Nenhuma inbox disponível.</p>
          </div>

          <!-- Automações -->
          <div>
            <p class="text-xs font-semibold uppercase tracking-wide text-n-slate-10 mb-3">Automações</p>
            <div class="space-y-3">

              <div class="flex items-start gap-3 px-3 py-2.5 rounded-lg border border-n-weak">
                <input id="s-autoCreateCard" v-model="settingsForm.automations.autoCreateCard" type="checkbox" class="mt-0.5 flex-shrink-0 cursor-pointer" />
                <label for="s-autoCreateCard" class="cursor-pointer">
                  <p class="text-sm font-medium text-n-slate-12">Criar card automaticamente</p>
                  <p class="text-xs text-n-slate-10 mt-0.5">Cria um card neste pipeline ao receber uma nova conversa nas inboxes vinculadas.</p>
                </label>
              </div>

              <div class="flex items-start gap-3 px-3 py-2.5 rounded-lg border border-n-weak">
                <input id="s-autoAssignAgent" v-model="settingsForm.automations.autoAssignAgent" type="checkbox" class="mt-0.5 flex-shrink-0 cursor-pointer" />
                <label for="s-autoAssignAgent" class="cursor-pointer">
                  <p class="text-sm font-medium text-n-slate-12">Atribuir agente automaticamente</p>
                  <p class="text-xs text-n-slate-10 mt-0.5">Atribui o agente responsável pela conversa ao card criado automaticamente.</p>
                </label>
              </div>

              <div class="flex items-start gap-3 px-3 py-2.5 rounded-lg border border-n-weak">
                <input id="s-autoMoveOnResolve" v-model="settingsForm.automations.autoMoveOnResolve" type="checkbox" class="mt-0.5 flex-shrink-0 cursor-pointer" />
                <label for="s-autoMoveOnResolve" class="cursor-pointer">
                  <p class="text-sm font-medium text-n-slate-12">Mover card ao resolver conversa</p>
                  <p class="text-xs text-n-slate-10 mt-0.5">Move o card para a última etapa do pipeline quando a conversa vinculada for resolvida.</p>
                </label>
              </div>

              <div class="flex items-start gap-3 px-3 py-2.5 rounded-lg border border-n-weak">
                <input id="s-autoMoveOnReopen" v-model="settingsForm.automations.autoMoveOnReopen" type="checkbox" class="mt-0.5 flex-shrink-0 cursor-pointer" />
                <label for="s-autoMoveOnReopen" class="cursor-pointer">
                  <p class="text-sm font-medium text-n-slate-12">Mover card ao reabrir conversa</p>
                  <p class="text-xs text-n-slate-10 mt-0.5">Move o card de volta para a primeira etapa quando a conversa vinculada for reaberta.</p>
                </label>
              </div>

              <div class="flex items-start gap-3 px-3 py-2.5 rounded-lg border border-n-weak">
                <input id="s-notifyAgentOnAssign" v-model="settingsForm.automations.notifyAgentOnAssign" type="checkbox" class="mt-0.5 flex-shrink-0 cursor-pointer" />
                <label for="s-notifyAgentOnAssign" class="cursor-pointer">
                  <p class="text-sm font-medium text-n-slate-12">Notificar agente ao ser atribuído</p>
                  <p class="text-xs text-n-slate-10 mt-0.5">Envia uma notificação ao agente quando for atribuído a um card neste pipeline.</p>
                </label>
              </div>

              <div class="flex items-start gap-3 px-3 py-2.5 rounded-lg border border-n-weak">
                <input id="s-notifyOnCardOverdue" v-model="settingsForm.automations.notifyOnCardOverdue" type="checkbox" class="mt-0.5 flex-shrink-0 cursor-pointer" />
                <label for="s-notifyOnCardOverdue" class="cursor-pointer">
                  <p class="text-sm font-medium text-n-slate-12">Notificar quando card vencer</p>
                  <p class="text-xs text-n-slate-10 mt-0.5">Envia uma notificação ao agente responsável quando a data de vencimento do card for atingida.</p>
                </label>
              </div>

              <div class="flex items-start gap-3 px-3 py-2.5 rounded-lg border border-n-weak">
                <input id="s-notifyOnStageChange" v-model="settingsForm.automations.notifyOnStageChange" type="checkbox" class="mt-0.5 flex-shrink-0 cursor-pointer" />
                <label for="s-notifyOnStageChange" class="cursor-pointer">
                  <p class="text-sm font-medium text-n-slate-12">Notificar ao mover entre etapas</p>
                  <p class="text-xs text-n-slate-10 mt-0.5">Envia uma notificação quando um card for movido de etapa neste pipeline.</p>
                </label>
              </div>

              <div class="flex items-start gap-3 px-3 py-2.5 rounded-lg border border-n-weak">
                <input id="s-showClosedConversations" v-model="settingsForm.automations.showClosedConversations" type="checkbox" class="mt-0.5 flex-shrink-0 cursor-pointer" />
                <label for="s-showClosedConversations" class="cursor-pointer">
                  <p class="text-sm font-medium text-n-slate-12">Exibir conversas encerradas no card</p>
                  <p class="text-xs text-n-slate-10 mt-0.5">Permite visualizar conversas com status "resolvido" na seção de conversas do card.</p>
                </label>
              </div>

            </div>
          </div>

          <!-- Webhooks -->
          <div>
            <p class="text-xs font-semibold uppercase tracking-wide text-n-slate-10 mb-2">Webhooks (n8n / integrações)</p>
            <p class="text-xs text-n-slate-11 mb-3">URLs de callback para receber eventos do pipeline. Configure uma URL por cliente ou fluxo n8n.</p>

            <!-- Webhooks cadastrados -->
            <div class="space-y-2 mb-3">
              <div
                v-for="wh in webhooks"
                :key="wh.id"
                class="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-n-weak bg-n-alpha-1"
              >
                <div class="flex-1 min-w-0">
                  <p class="text-xs font-medium text-n-slate-12 truncate">{{ wh.url }}</p>
                  <p v-if="wh.description" class="text-[10px] text-n-slate-10 mt-0.5">{{ wh.description }}</p>
                  <div class="flex flex-wrap gap-1 mt-1">
                    <span v-for="ev in wh.events" :key="ev" class="px-1.5 py-0.5 text-[9px] rounded-full bg-n-alpha-2 text-n-slate-11">{{ ev }}</span>
                  </div>
                </div>
                <button
                  class="flex-shrink-0 px-2 py-1 text-xs rounded border border-n-brand text-n-brand cursor-pointer hover:bg-n-alpha-1"
                  @click="removeWebhook(wh.id)"
                >🗑</button>
              </div>
              <p v-if="!webhooks.length" class="text-xs text-n-slate-10 py-2">Nenhum webhook cadastrado.</p>
            </div>

            <!-- Formulário: novo webhook -->
            <div class="rounded-lg border border-n-weak p-3 space-y-2">
              <p class="text-[10px] font-semibold uppercase tracking-wide text-n-slate-10">Novo webhook</p>
              <input
                v-model="webhookNewUrl"
                placeholder="https://n8n.exemplo.io/webhook/pipeline-events"
                class="w-full px-2 py-1.5 text-xs rounded border border-n-weak bg-n-surface-1 text-n-slate-12 focus:outline-none focus:border-n-brand"
              />
              <input
                v-model="webhookNewDesc"
                placeholder="Descrição (opcional, ex: cliente XPTO)"
                class="w-full px-2 py-1.5 text-xs rounded border border-n-weak bg-n-surface-1 text-n-slate-12 focus:outline-none focus:border-n-brand"
              />
              <div class="flex flex-wrap gap-2">
                <label v-for="ev in allWebhookEvents" :key="ev" class="flex items-center gap-1 text-[10px] text-n-slate-12 cursor-pointer select-none">
                  <input type="checkbox" :value="ev" v-model="webhookNewEvents" class="cursor-pointer" />
                  {{ ev }}
                </label>
              </div>
              <button
                class="px-3 py-1.5 text-xs text-white rounded-md bg-n-solid-blue cursor-pointer hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="!webhookNewUrl.trim() || !webhookNewEvents.length"
                @click="addWebhook"
              >+ Adicionar webhook</button>
            </div>
          </div>

        </div>

        <!-- Footer -->
        <div class="px-5 py-3 border-t border-n-weak flex-shrink-0 flex justify-end gap-2">
          <button class="px-3 py-1.5 text-sm rounded border border-n-weak text-n-slate-12 cursor-pointer hover:bg-n-alpha-1" @click="settingsModal = false">Cancelar</button>
          <button class="px-3 py-1.5 text-sm text-white rounded-md bg-n-solid-blue cursor-pointer hover:brightness-110 active:scale-95" @click="saveSettings">Salvar configurações</button>
        </div>
      </div>
    </div>

    <!-- Modal: Catálogo de Produtos -->
    <div v-if="productMgmtModal" class="fixed inset-0 z-[60] flex items-center justify-center" style="background: rgba(55, 65, 81, 0.80)" @click.self="productMgmtModal = false">
      <div class="w-[34rem] rounded-xl bg-n-solid-1 border border-n-weak max-h-[85vh] flex flex-col">

        <!-- Header -->
        <div class="px-5 pt-4 pb-3 border-b border-n-weak flex-shrink-0 flex items-center justify-between">
          <div>
            <h3 class="text-base font-semibold text-n-slate-12">Catálogo de Produtos</h3>
            <p class="mt-0.5 text-xs text-n-slate-11">{{ current?.name }}</p>
          </div>
          <span class="text-xs text-n-slate-10 bg-n-alpha-2 px-2 py-0.5 rounded-full">{{ catalogProducts.length }} produto(s)</span>
        </div>

        <!-- Formulário de cadastro / edição -->
        <div class="flex-shrink-0 px-5 py-3 border-b border-n-weak bg-n-alpha-1/40">
          <p class="text-[10px] font-semibold uppercase tracking-wide text-n-slate-10 mb-2">
            {{ productMgmtEditId ? 'Editar produto' : 'Novo produto' }}
          </p>
          <div class="flex gap-2 items-start">
            <div class="flex-1">
              <label class="block mb-0.5 text-[10px] text-n-slate-10">Nome *</label>
              <input
                v-model="productMgmtForm.name"
                placeholder="Nome do produto"
                class="w-full px-2 py-1.5 text-sm rounded border border-n-weak bg-n-surface-1 text-n-slate-12 focus:outline-none focus:border-n-brand"
              />
            </div>
            <div class="w-32 flex-shrink-0">
              <label class="block mb-0.5 text-[10px] text-n-slate-10">Preço unitário (R$)</label>
              <input
                v-model.number="productMgmtForm.unitPrice"
                type="number" min="0" step="0.01"
                placeholder="0,00"
                class="w-full px-2 py-1.5 text-sm rounded border border-n-weak bg-n-surface-1 text-n-slate-12 focus:outline-none focus:border-n-brand"
              />
            </div>
          </div>
          <div class="mt-2">
            <label class="block mb-0.5 text-[10px] text-n-slate-10">Descrição</label>
            <input
              v-model="productMgmtForm.description"
              placeholder="Descrição do produto (opcional)"
              class="w-full px-2 py-1.5 text-sm rounded border border-n-weak bg-n-surface-1 text-n-slate-12 focus:outline-none focus:border-n-brand"
            />
          </div>
          <div class="flex gap-2 mt-2 justify-end">
            <button v-if="productMgmtEditId" class="px-3 py-1.5 text-xs rounded border border-n-weak text-n-slate-12 cursor-pointer hover:bg-n-alpha-1" @click="cancelProductMgmtEdit">Cancelar edição</button>
            <button
              class="px-3 py-1.5 text-xs text-white rounded-md bg-n-solid-blue cursor-pointer select-none transition-all hover:brightness-110 active:scale-95"
              :disabled="!productMgmtForm.name.trim()"
              @click="saveProductMgmt"
            >{{ productMgmtEditId ? 'Salvar alterações' : '+ Adicionar produto' }}</button>
          </div>
        </div>

        <!-- Lista de produtos -->
        <div class="flex-1 overflow-y-auto px-5 py-3 space-y-1.5">
          <div
            v-for="p in catalogProducts"
            :key="p.id"
            class="flex items-center gap-3 px-3 py-2.5 rounded-lg border bg-n-surface-1 transition-colors"
            :class="productMgmtEditId === p.id ? 'border-n-brand bg-n-alpha-1' : 'border-n-weak'"
          >
            <div class="flex-1 min-w-0">
              <div class="text-sm font-medium text-n-slate-12 truncate">{{ p.name }}</div>
              <div v-if="p.description" class="text-xs text-n-slate-10 truncate">{{ p.description }}</div>
            </div>
            <span class="flex-shrink-0 text-sm font-semibold text-n-slate-12">{{ money(p.unitPrice, current.currency) }}</span>
            <button title="Editar produto" class="flex-shrink-0 px-2 py-1 text-xs rounded border border-n-weak text-n-slate-11 cursor-pointer hover:bg-n-alpha-1" @click="editProductMgmt(p)">✎</button>
            <button title="Excluir produto" class="flex-shrink-0 px-2 py-1 text-xs rounded border border-n-brand text-n-brand cursor-pointer hover:bg-n-alpha-1" @click="deleteProductMgmt(p.id)">🗑</button>
          </div>
          <p v-if="!catalogProducts.length" class="text-xs text-center text-n-slate-10 py-8">
            Nenhum produto cadastrado ainda.<br />Use o formulário acima para adicionar.
          </p>
        </div>

        <!-- Footer -->
        <div class="px-5 py-3 border-t border-n-weak flex-shrink-0 flex justify-end">
          <button class="px-3 py-1.5 text-sm rounded border border-n-weak text-n-slate-12 cursor-pointer hover:bg-n-alpha-1" @click="productMgmtModal = false">Fechar</button>
        </div>
      </div>
    </div>

    <!-- Modal: Funil -->
    <div v-if="pipeModal" class="fixed inset-0 z-50 flex items-center justify-center" style="background: rgba(55, 65, 81, 0.80)" @click.self="pipeModal = false">
      <div class="w-96 p-5 rounded-xl bg-n-solid-1 border border-n-weak">
        <h3 class="mb-3 text-base font-semibold text-n-slate-12">Novo funil</h3>
        <label class="block mb-1 text-xs text-n-slate-11">Nome</label>
        <input v-model="pipeForm.name" class="w-full px-2 py-1.5 mb-2 text-sm rounded border border-n-weak bg-n-surface-1 text-n-slate-12 focus:outline-none focus:border-n-brand" />
        <label class="block mb-1 text-xs text-n-slate-11">Moeda</label>
        <input v-model="pipeForm.currency" class="w-full px-2 py-1.5 text-sm rounded border border-n-weak bg-n-surface-1 text-n-slate-12 focus:outline-none focus:border-n-brand" />
        <div class="flex justify-end gap-2 mt-4">
          <button class="px-3 py-1.5 text-sm rounded border border-n-weak text-n-slate-12 cursor-pointer select-none transition-colors hover:bg-n-alpha-1" @click="pipeModal = false">Cancelar</button>
          <button class="px-3 py-1.5 text-sm text-white rounded bg-n-solid-blue cursor-pointer select-none transition-all hover:brightness-110 active:scale-95" @click="savePipeline">Criar</button>
        </div>
      </div>
    </div>

    <!-- Modal: Etapa -->
    <div v-if="stageModal" class="fixed inset-0 z-50 flex items-center justify-center" style="background: rgba(55, 65, 81, 0.80)" @click.self="stageModal = false">
      <div class="w-96 p-5 rounded-xl bg-n-solid-1 border border-n-weak">
        <h3 class="mb-3 text-base font-semibold text-n-slate-12">{{ stageForm.id ? 'Editar etapa' : 'Nova etapa' }}</h3>
        <label class="block mb-1 text-xs text-n-slate-11">Nome</label>
        <input v-model="stageForm.name" class="w-full px-2 py-1.5 mb-2 text-sm rounded border border-n-weak bg-n-surface-1 text-n-slate-12 focus:outline-none focus:border-n-brand" />
        <label class="block mb-1 text-xs text-n-slate-11">Cor</label>
        <input v-model="stageForm.color" type="color" class="h-9 w-16 mb-2 cursor-pointer rounded" />
        <label class="block mb-1 text-xs text-n-slate-11">Probabilidade de ganho (%)</label>
        <input v-model.number="stageForm.winProbability" type="number" class="w-full px-2 py-1.5 mb-2 text-sm rounded border border-n-weak bg-n-surface-1 text-n-slate-12 focus:outline-none focus:border-n-brand" />
        <label class="flex items-center gap-2 mb-1 text-sm text-n-slate-12 cursor-pointer">
          <input v-model="stageForm.isWon" type="checkbox" class="cursor-pointer" /> Etapa "Ganha"
        </label>
        <label class="flex items-center gap-2 text-sm text-n-slate-12 cursor-pointer">
          <input v-model="stageForm.isLost" type="checkbox" class="cursor-pointer" /> Etapa "Perdida"
        </label>
        <div class="flex justify-between mt-4">
          <button v-if="stageForm.id" class="px-3 py-1.5 text-sm text-white rounded bg-red-600 cursor-pointer hover:bg-red-700" @click="delStage">Excluir</button>
          <span v-else />
          <span class="flex gap-2">
            <button class="px-3 py-1.5 text-sm rounded border border-n-weak text-n-slate-12 cursor-pointer hover:bg-n-alpha-1" @click="stageModal = false">Cancelar</button>
            <button class="px-3 py-1.5 text-sm text-white rounded bg-n-solid-blue cursor-pointer hover:brightness-110 active:scale-95" @click="saveStage">Salvar</button>
          </span>
        </div>
      </div>
    </div>

    <!-- Modal: Transferir card para outro funil -->
    <div v-if="transferModal" class="fixed inset-0 z-[60] flex items-center justify-center p-4" style="background: rgba(55, 65, 81, 0.80)" @click.self="closeTransfer">
      <div class="w-[30rem] max-w-full flex flex-col rounded-xl bg-n-solid-1 border border-n-weak shadow-xl">
        <!-- Cabeçalho -->
        <div class="px-5 pt-5 pb-3 border-b border-n-weak flex items-center gap-2.5">
          <span class="flex items-center justify-center w-7 h-7 rounded-lg bg-n-brand/15 text-n-brand text-sm font-semibold flex-shrink-0" aria-hidden="true">⇄</span>
          <h3 class="flex-1 text-base font-semibold text-n-slate-12">Transferir card de funil</h3>
          <button title="Fechar" class="text-n-slate-11 cursor-pointer hover:text-n-slate-12" @click="closeTransfer">✕</button>
        </div>
        <!-- Card / origem -->
        <div v-if="transferCard" class="px-5 py-2.5 text-xs text-n-slate-11 bg-n-alpha-1 border-b border-n-weak/60 flex flex-wrap items-center gap-x-2 gap-y-1">
          <span>Card:</span>
          <span class="font-medium text-n-slate-12 truncate max-w-[55%]" :title="transferCard.title">{{ transferCard.title }}</span>
          <span v-if="current" class="ml-auto">Origem: <span class="font-medium text-n-slate-12">{{ current.name }}</span></span>
        </div>
        <!-- Corpo: 2 dropdowns -->
        <div class="p-5 space-y-4">
          <!-- Dropdown 1: funil de destino -->
          <div>
            <label class="flex items-center gap-1.5 mb-1.5 text-sm font-medium text-n-slate-12">
              Funil de destino <span class="text-red-500">*</span>
              <span class="cursor-help text-xs text-n-slate-10 hover:text-n-slate-12" title="Escolha o funil (Kanban) para onde o card será movido. A lista mostra apenas funis diferentes do funil atual.">ⓘ</span>
            </label>
            <select
              v-model="transferPipelineId"
              title="Selecione o funil (Kanban) de destino"
              class="w-full py-2 px-3 text-sm rounded-lg border border-n-weak bg-n-surface-1 text-n-slate-12 cursor-pointer transition-colors focus:outline-none focus:border-n-brand focus:ring-1 focus:ring-n-brand"
              @change="onTransferPipelineChange"
            >
              <option :value="null" disabled>Selecione um funil…</option>
              <option v-for="p in transferTargets()" :key="p.id" :value="p.id">{{ p.name }}</option>
            </select>
            <p v-if="!transferTargets().length" class="mt-1.5 text-[11px] text-amber-600">Nenhum outro funil disponível para transferência.</p>
            <p v-else class="mt-1.5 text-[11px] text-n-slate-10">Para qual Kanban o card será movido.</p>
          </div>
          <!-- Dropdown 2: etapa de destino (carrega após escolher o funil) -->
          <div>
            <label class="flex items-center gap-1.5 mb-1.5 text-sm font-medium" :class="transferPipelineId ? 'text-n-slate-12' : 'text-n-slate-10'">
              Etapa de destino <span class="text-red-500">*</span>
              <span class="cursor-help text-xs text-n-slate-10 hover:text-n-slate-12" title="A etapa (coluna) dentro do funil escolhido onde o card será posicionado. As etapas são carregadas automaticamente após escolher o funil acima.">ⓘ</span>
            </label>
            <select
              v-model="transferStageId"
              :disabled="!transferPipelineId || transferLoadingStages"
              :title="transferPipelineId ? 'Selecione a etapa de destino' : 'Escolha primeiro o funil de destino'"
              class="w-full py-2 px-3 text-sm rounded-lg border border-n-weak bg-n-surface-1 text-n-slate-12 cursor-pointer transition-colors focus:outline-none focus:border-n-brand focus:ring-1 focus:ring-n-brand disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option :value="null" disabled>{{ stagePlaceholder() }}</option>
              <option v-for="s in transferStages" :key="s.id" :value="s.id">{{ s.name }}</option>
            </select>
            <div v-if="selectedTransferStage()" class="mt-1.5 flex items-center gap-1.5 text-[11px] text-n-slate-10">
              <span class="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0" :style="{ background: selectedTransferStage().color || '#94a3b8' }" />
              <span>O card entrará na etapa "<span class="font-medium text-n-slate-12">{{ selectedTransferStage().name }}</span>".</span>
            </div>
            <p v-else class="mt-1.5 text-[11px] text-n-slate-10">Escolha o funil acima para liberar as etapas.</p>
          </div>
        </div>
        <!-- Rodapé -->
        <div class="px-5 py-4 border-t border-n-weak flex items-center justify-end gap-2">
          <button
            class="px-3 py-1.5 text-sm rounded-lg border border-n-weak text-n-slate-11 cursor-pointer transition-colors hover:bg-n-alpha-1 active:bg-n-alpha-2"
            @click="closeTransfer"
          >
            Cancelar
          </button>
          <button
            :disabled="!transferPipelineId || !transferStageId || transferSubmitting"
            :title="(!transferPipelineId || !transferStageId) ? 'Escolha o funil e a etapa de destino para habilitar' : 'Transferir o card para o funil/etapa selecionado'"
            class="px-4 py-1.5 text-sm font-medium text-white rounded-lg bg-n-brand cursor-pointer transition-all hover:brightness-110 active:brightness-90 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:brightness-100 disabled:active:scale-100"
            @click="confirmTransfer"
          >
            {{ transferSubmitting ? 'Transferindo…' : 'Transferir' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal: Card -->
    <div v-if="cardModal" class="fixed inset-0 z-50 flex items-center justify-center" style="background: rgba(55, 65, 81, 0.80)" @click.self="cardModal = false">
      <div class="w-[32rem] rounded-xl bg-n-solid-1 border border-n-weak max-h-[92vh] flex flex-col">
        <!-- Header -->
        <div class="px-5 pt-5 pb-3 border-b border-n-weak flex items-center gap-3">
          <h3 class="flex-1 text-base font-semibold text-n-slate-12">{{ cardForm.id ? 'Editar card' : 'Novo card' }}</h3>
          <span v-if="cardForm.status === 'WON'" class="px-2 py-0.5 text-xs font-bold text-white rounded-full bg-green-600">✔ GANHO</span>
          <span v-if="cardForm.status === 'LOST'" class="px-2 py-0.5 text-xs font-bold text-white rounded-full bg-red-600">✕ PERDIDO</span>
        </div>

        <!-- Body scrollável -->
        <div class="flex-1 overflow-auto px-4 py-3 space-y-2">

          <!-- Título (sempre visível) -->
          <div class="px-1">
            <label class="block mb-1 text-xs font-medium text-n-slate-11">Título</label>
            <input v-model="cardForm.title" placeholder="Título do card" class="w-full px-2 py-1.5 text-sm rounded border border-n-weak bg-n-surface-1 text-n-slate-12 focus:outline-none focus:border-n-brand" />
          </div>

          <!-- ── Seção: Descrição ── -->
          <div class="rounded-lg border border-n-weak overflow-hidden">
            <button
              class="w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-1 transition-colors cursor-pointer select-none"
              @click="toggleSection('descricao')"
            >
              <span>Descrição</span>
              <span class="text-n-slate-10 text-xs">{{ sectionOpen.descricao ? '▲' : '▼' }}</span>
            </button>
            <div v-show="sectionOpen.descricao" class="px-2 pb-3 pt-2 border-t border-n-weak">
              <FullEditor
                :model-value="cardForm.description"
                :editor-id="`card-desc-${cardForm.id || 'new'}`"
                placeholder="Notas e observações..."
                :autofocus="false"
                :enabled-menu-options="DESCRIPTION_MENU_OPTIONS"
                class="kanban-description-editor"
                @update:model-value="v => { cardForm.description = v }"
              />
            </div>
          </div>

          <!-- ── Seção: Oportunidade ── -->
          <div class="rounded-lg border border-n-weak overflow-hidden">
            <button
              class="w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-1 transition-colors cursor-pointer select-none"
              @click="toggleSection('oportunidade')"
            >
              <span>Oportunidade</span>
              <span class="text-n-slate-10 text-xs">{{ sectionOpen.oportunidade ? '▲' : '▼' }}</span>
            </button>
            <div v-show="sectionOpen.oportunidade" class="px-3 pb-3 pt-2 border-t border-n-weak space-y-3">

              <!-- Valor manual -->
              <div>
                <label class="block mb-1 text-xs text-n-slate-11">Valor total (R$)</label>
                <input v-model.number="cardForm.value" type="number" min="0" step="0.01" placeholder="0,00" class="w-full px-2 py-1.5 text-sm rounded border border-n-weak bg-n-surface-1 text-n-slate-12 focus:outline-none focus:border-n-brand" />
              </div>

              <!-- Produtos do catálogo -->
              <div>
                <div class="flex items-center justify-between mb-1.5">
                  <label class="text-xs text-n-slate-11">Produtos do catálogo</label>
                  <button
                    class="flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded border border-n-weak text-n-slate-11 cursor-pointer hover:bg-n-alpha-1 transition-colors"
                    title="Cadastrar e gerenciar produtos do catálogo"
                    @click="openProductMgmt"
                  >⚙ Gerenciar catálogo</button>
                </div>
                <div class="flex gap-1.5 flex-wrap items-end mb-2">
                  <div class="flex-1 min-w-[140px]">
                    <select v-model="productPickId" class="w-full px-2 py-1.5 text-sm rounded border border-n-weak bg-n-surface-1 text-n-slate-12 cursor-pointer focus:outline-none focus:border-n-brand">
                      <option :value="null">Selecionar produto...</option>
                      <option v-for="p in catalogProducts" :key="p.id" :value="p.id">
                        {{ p.name }} — {{ money(p.unitPrice, current.currency) }}
                      </option>
                    </select>
                  </div>
                  <div class="w-20">
                    <label class="block mb-0.5 text-[10px] text-n-slate-10">Qtd</label>
                    <input v-model.number="productPickQty" type="number" min="0.01" step="0.01" class="w-full px-2 py-1.5 text-sm rounded border border-n-weak bg-n-surface-1 text-n-slate-12 focus:outline-none focus:border-n-brand" />
                  </div>
                  <div class="w-20">
                    <label class="block mb-0.5 text-[10px] text-n-slate-10">Desc %</label>
                    <input v-model.number="productPickDiscount" type="number" min="0" max="100" step="0.01" class="w-full px-2 py-1.5 text-sm rounded border border-n-weak bg-n-surface-1 text-n-slate-12 focus:outline-none focus:border-n-brand" />
                  </div>
                  <button class="px-3 py-1.5 text-xs text-white rounded-md bg-n-solid-blue cursor-pointer select-none transition-all hover:brightness-110 active:scale-95 flex-shrink-0" :disabled="!productPickId" @click="addProductLine">+ Adicionar</button>
                </div>
                <div v-if="(cardForm.selectedProducts || []).length" class="space-y-1">
                  <div v-for="(item, idx) in cardForm.selectedProducts" :key="idx" class="flex items-center gap-2 px-2.5 py-2 rounded-lg border border-n-weak bg-n-alpha-1 text-xs">
                    <span class="flex-1 font-medium text-n-slate-12 truncate">{{ item.productName }}</span>
                    <span class="text-n-slate-10 flex-shrink-0">{{ item.quantity }}×</span>
                    <span class="text-n-slate-11 flex-shrink-0">{{ money(item.unitPrice, current.currency) }}</span>
                    <span v-if="item.discountPercentage" class="text-orange-600 flex-shrink-0 text-[10px]">-{{ item.discountPercentage }}%</span>
                    <span class="font-semibold text-n-slate-12 flex-shrink-0">{{ money(productLineSubtotal(item), current.currency) }}</span>
                    <button class="flex-shrink-0 text-n-slate-10 hover:text-red-500 cursor-pointer text-base leading-none" @click="removeProductLine(idx)">×</button>
                  </div>
                  <div class="flex justify-end pt-1 pr-1">
                    <span class="text-xs font-semibold text-n-slate-12">Total produtos: {{ money(productsTotal, current.currency) }}</span>
                  </div>
                </div>
                <p v-else class="text-xs text-n-slate-10 mt-1">
                  {{ catalogProducts.length ? 'Nenhum produto adicionado.' : 'Nenhum produto no catálogo deste funil.' }}
                </p>
              </div>
            </div>
          </div>

          <!-- ── Seção: Relacionamentos ── -->
          <!-- overflow-visible: deixa os dropdowns de busca (agente/contato/conversa) escaparem da seção em vez de serem cortados/cobertos pela seção seguinte -->
          <div class="rounded-lg border border-n-weak overflow-visible">
            <button
              class="w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-1 transition-colors cursor-pointer select-none"
              @click="toggleSection('relacionamentos')"
            >
              <span>Relacionamentos</span>
              <span class="text-n-slate-10 text-xs">{{ sectionOpen.relacionamentos ? '▲' : '▼' }}</span>
            </button>
            <div v-show="sectionOpen.relacionamentos" class="px-3 pb-3 pt-2 border-t border-n-weak space-y-3">

              <!-- Agentes responsáveis -->
              <div>
                <div class="flex items-center justify-between mb-1">
                  <label class="text-xs text-n-slate-11">Agentes responsáveis</label>
                  <button class="text-xs text-n-brand hover:underline cursor-pointer" @click="assignToMe">Atribuir a mim</button>
                </div>
                <div v-if="selectedAssignees.length" class="flex flex-wrap gap-1 mb-1.5">
                  <span v-for="agent in selectedAssignees" :key="agent.id" class="flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-n-alpha-2 text-n-slate-12">
                    {{ agent.name }}
                    <button class="text-n-slate-10 hover:text-red-500 cursor-pointer leading-none" @click="removeAssignee(agent.id)">×</button>
                  </span>
                </div>
                <div class="relative">
                  <input v-model="agentSearch" placeholder="Buscar agente..." class="w-full px-2 py-1.5 text-sm rounded border border-n-weak bg-n-surface-1 text-n-slate-12 focus:outline-none focus:border-n-brand" @focus="showAgentDropdown = true" @blur="hideAssigneeDropdown" />
                  <div v-if="showAgentDropdown && filteredAgentsForAssignee.length" class="absolute z-20 w-full mt-0.5 bg-n-solid-1 border border-n-weak rounded-lg shadow-lg max-h-36 overflow-auto">
                    <button v-for="agent in filteredAgentsForAssignee" :key="agent.id" class="w-full px-3 py-1.5 text-sm text-left text-n-slate-12 hover:bg-n-alpha-1 cursor-pointer" @mousedown.prevent="toggleAssignee(agent.id)">{{ agent.name }}</button>
                  </div>
                </div>
              </div>

              <!-- Participantes -->
              <div>
                <label class="block mb-1 text-xs text-n-slate-11">Participantes</label>
                <div v-if="selectedParticipants.length" class="flex flex-wrap gap-1 mb-1.5">
                  <span v-for="agent in selectedParticipants" :key="agent.id" class="flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-n-alpha-2 text-n-slate-12">
                    {{ agent.name }}
                    <button class="text-n-slate-10 hover:text-red-500 cursor-pointer leading-none" @click="removeParticipant(agent.id)">×</button>
                  </span>
                </div>
                <div class="relative">
                  <input v-model="agentSearch" placeholder="Buscar participante..." class="w-full px-2 py-1.5 text-sm rounded border border-n-weak bg-n-surface-1 text-n-slate-12 focus:outline-none focus:border-n-brand" @focus="showParticipantDropdown = true" @blur="hideParticipantDropdown" />
                  <div v-if="showParticipantDropdown && filteredAgentsForParticipant.length" class="absolute z-20 w-full mt-0.5 bg-n-solid-1 border border-n-weak rounded-lg shadow-lg max-h-36 overflow-auto">
                    <button v-for="agent in filteredAgentsForParticipant" :key="agent.id" class="w-full px-3 py-1.5 text-sm text-left text-n-slate-12 hover:bg-n-alpha-1 cursor-pointer" @mousedown.prevent="toggleParticipant(agent.id)">{{ agent.name }}</button>
                  </div>
                </div>
              </div>

              <!-- Etiquetas -->
              <div>
                <label class="block mb-1 text-xs text-n-slate-11">Etiquetas</label>
                <div v-if="kanbanLabels.length" class="flex flex-wrap gap-1.5">
                  <button
                    v-for="lbl in kanbanLabels"
                    :key="lbl.id"
                    class="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border transition-colors cursor-pointer"
                    :style="(cardForm.selectedLabels || []).includes(lbl.title)
                      ? { background: lbl.color, borderColor: lbl.color, color: '#fff' }
                      : { borderColor: lbl.color, color: lbl.color, background: 'transparent' }"
                    @click="toggleCardLabel(lbl.title)"
                  >
                    <span
                      class="w-1.5 h-1.5 rounded-full flex-shrink-0"
                      :style="{ background: (cardForm.selectedLabels || []).includes(lbl.title) ? '#fff' : lbl.color }"
                    />
                    {{ lbl.title }}
                  </button>
                </div>
                <p v-else class="text-xs text-n-slate-9 italic">
                  Nenhuma etiqueta cadastrada. Crie em Configurações → Etiquetas.
                </p>
              </div>

              <!-- Contatos -->
              <div>
                <label class="block mb-1 text-xs text-n-slate-11">Contatos</label>
                <div v-if="(cardForm.selectedContacts || []).length" class="flex flex-wrap gap-1 mb-1.5">
                  <span v-for="c in cardForm.selectedContacts" :key="c.id" class="flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-n-alpha-2 text-n-slate-12">
                    {{ c.name }}
                    <button class="text-n-slate-10 hover:text-red-500 cursor-pointer leading-none" @click="removeContact(c.id)">×</button>
                  </span>
                </div>
                <div class="relative">
                  <input v-model="contactSearch" placeholder="Buscar contato pelo nome..." class="w-full px-2 py-1.5 text-sm rounded border border-n-weak bg-n-surface-1 text-n-slate-12 focus:outline-none focus:border-n-brand" @input="onContactSearch" />
                  <div v-if="contactResults.length" class="absolute z-20 w-full mt-0.5 bg-n-solid-1 border border-n-weak rounded-lg shadow-lg max-h-36 overflow-auto">
                    <button v-for="contact in contactResults" :key="contact.id" class="w-full px-3 py-1.5 text-sm text-left text-n-slate-12 hover:bg-n-alpha-1 cursor-pointer" @mousedown.prevent="addContact(contact)">
                      <span class="font-medium">{{ contact.name }}</span>
                      <span v-if="contact.phone_number" class="ml-1 text-xs text-n-slate-10">{{ contact.phone_number }}</span>
                    </button>
                  </div>
                  <span v-if="contactSearching" class="absolute right-2 top-2 text-xs text-n-slate-10">...</span>
                </div>
              </div>

              <!-- Conversas -->
              <div>
                <label class="block mb-1 text-xs text-n-slate-11">Conversas</label>
                <div v-if="(cardForm.selectedConversations || []).length" class="flex flex-wrap gap-1 mb-1.5">
                  <span v-for="cv in cardForm.selectedConversations" :key="cv.id" class="flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                    {{ cv.label }}
                    <button class="text-blue-400 hover:text-red-500 cursor-pointer leading-none" @click="removeConversation(cv.id)">×</button>
                  </span>
                </div>
                <div class="relative">
                  <input v-model="conversationSearch" placeholder="Buscar conversa (#id ou nome do contato)..." class="w-full px-2 py-1.5 text-sm rounded border border-n-weak bg-n-surface-1 text-n-slate-12 focus:outline-none focus:border-n-brand" @input="onConversationSearch" />
                  <div v-if="conversationResults.length" class="absolute z-20 w-full mt-0.5 bg-n-solid-1 border border-n-weak rounded-lg shadow-lg max-h-36 overflow-auto">
                    <button v-for="conv in conversationResults" :key="conv.id" class="w-full px-3 py-1.5 text-sm text-left text-n-slate-12 hover:bg-n-alpha-1 cursor-pointer" @mousedown.prevent="addConversation(conv)">
                      <span class="font-medium text-blue-600">#{{ conv.id }}</span>
                      <span v-if="conv.contact?.name" class="ml-1 text-n-slate-11">— {{ conv.contact.name }}</span>
                      <span v-if="conv.contact?.phone_number" class="ml-1 text-xs text-n-slate-10">{{ conv.contact.phone_number }}</span>
                      <span v-if="conv.inbox?.name" class="ml-1 text-xs text-n-slate-10">· {{ conv.inbox.name }}</span>
                    </button>
                  </div>
                  <span v-if="conversationSearching" class="absolute right-2 top-2 text-xs text-n-slate-10">...</span>
                </div>
              </div>
            </div>
          </div>

          <!-- ── Seção: Agenda ── -->
          <div class="rounded-lg border border-n-weak overflow-hidden">
            <button
              class="w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-1 transition-colors cursor-pointer select-none"
              @click="toggleSection('agenda')"
            >
              <span>Agenda</span>
              <span class="text-n-slate-10 text-xs">{{ sectionOpen.agenda ? '▲' : '▼' }}</span>
            </button>
            <div v-show="sectionOpen.agenda" class="px-3 pb-3 pt-2 border-t border-n-weak space-y-3">
              <div>
                <label class="block mb-1 text-xs text-n-slate-11">Data de início</label>
                <div class="flex gap-2">
                  <input v-model="cardForm.startDate" type="date" class="flex-1 px-2 py-1.5 text-sm rounded border border-n-weak bg-n-surface-1 text-n-slate-12 focus:outline-none focus:border-n-brand" />
                  <input v-model="cardForm.startTime" type="time" class="w-28 px-2 py-1.5 text-sm rounded border border-n-weak bg-n-surface-1 text-n-slate-12 focus:outline-none focus:border-n-brand" />
                </div>
              </div>
              <div>
                <label class="block mb-1 text-xs text-n-slate-11">Data de vencimento</label>
                <div class="flex gap-2">
                  <input v-model="cardForm.dueDate" type="date" class="flex-1 px-2 py-1.5 text-sm rounded border border-n-weak bg-n-surface-1 text-n-slate-12 focus:outline-none focus:border-n-brand" />
                  <input v-model="cardForm.dueTime" type="time" class="w-28 px-2 py-1.5 text-sm rounded border border-n-weak bg-n-surface-1 text-n-slate-12 focus:outline-none focus:border-n-brand" />
                </div>
              </div>
            </div>
          </div>

        </div>

        <!-- Footer fixo -->
        <div class="px-5 py-3 border-t border-n-weak flex flex-wrap items-center gap-2">
          <!-- Won/Lost/Reopen (apenas cards existentes) -->
          <template v-if="cardForm.id">
            <button
              v-if="cardForm.status !== 'WON'"
              class="px-2.5 py-1.5 text-xs font-semibold text-white rounded bg-green-600 cursor-pointer hover:bg-green-700 active:scale-95 transition-all"
              title="Marcar como Ganho"
              @click="closeCard('won')"
            >✔ Ganho</button>
            <button
              v-if="cardForm.status !== 'LOST'"
              class="px-2.5 py-1.5 text-xs font-semibold text-white rounded bg-red-600 cursor-pointer hover:bg-red-700 active:scale-95 transition-all"
              title="Marcar como Perdido"
              @click="closeCard('lost')"
            >✕ Perdido</button>
            <button
              v-if="cardForm.status === 'WON' || cardForm.status === 'LOST'"
              class="px-2.5 py-1.5 text-xs rounded border border-n-weak text-n-slate-12 cursor-pointer hover:bg-n-alpha-1 transition-colors"
              title="Reabrir card"
              @click="closeCard('reopen')"
            >↩ Reabrir</button>
            <div class="w-px h-5 bg-n-weak flex-shrink-0" />
          </template>
          <!-- Etapa -->
          <select v-model="cardForm.stageId" class="flex-1 min-w-[100px] px-2 py-1.5 text-sm rounded border border-n-weak bg-n-surface-1 text-n-slate-12 cursor-pointer focus:outline-none focus:border-n-brand">
            <option v-for="s in current.stages" :key="s.id" :value="s.id">{{ s.name }}</option>
          </select>
          <!-- Prioridade -->
          <select v-model="cardForm.priority" class="w-28 px-2 py-1.5 text-sm rounded border border-n-weak bg-n-surface-1 text-n-slate-12 cursor-pointer focus:outline-none focus:border-n-brand">
            <option :value="null">Nenhuma</option>
            <option value="low">Baixa</option>
            <option value="medium">Média</option>
            <option value="high">Alta</option>
            <option value="urgent">Urgente</option>
          </select>
          <button class="px-3 py-1.5 text-sm rounded border border-n-weak text-n-slate-12 cursor-pointer hover:bg-n-alpha-1" @click="cardModal = false">Cancelar</button>
          <button class="px-3 py-1.5 text-sm text-white rounded bg-n-solid-blue cursor-pointer hover:brightness-110 active:scale-95" @click="saveCard">Salvar</button>
        </div>
      </div>
    </div>

    <!-- Modal: Timeline -->
    <div v-if="timelineModal" class="fixed inset-0 z-50 flex items-center justify-center" style="background: rgba(55, 65, 81, 0.80)" @click.self="timelineModal = null">
      <div class="w-[32rem] rounded-xl bg-n-solid-1 border border-n-weak max-h-[85vh] flex flex-col">

        <!-- Header -->
        <div class="px-5 pt-4 pb-3 border-b border-n-weak flex-shrink-0">
          <h3 class="text-base font-semibold text-n-slate-12">Histórico do Card</h3>
          <p class="mt-0.5 text-xs text-n-slate-11 truncate">{{ timelineModal.title }}</p>
        </div>

        <!-- Lista de eventos -->
        <div class="flex-1 overflow-y-auto px-4 py-3 space-y-2">
          <div
            v-for="e in timeline"
            :key="e.id"
            class="rounded-lg border border-n-weak bg-n-surface-1 overflow-hidden border-l-4"
            :class="actionColor(e.action)"
          >
            <!-- Linha principal -->
            <div class="flex items-center gap-2.5 px-3 py-2.5">
              <span class="text-base flex-shrink-0">{{ actionIcon(e.action) }}</span>
              <div class="flex-1 min-w-0">
                <div class="flex items-baseline gap-1.5 flex-wrap">
                  <span class="text-xs font-semibold text-n-slate-12">{{ actionLabel(e.action) }} em:</span>
                  <span class="text-xs font-medium text-n-slate-12">{{ fmt(e.createdAt) }}</span>
                </div>
                <div v-if="e.performedByAgentId" class="mt-0.5 text-[10px] text-n-slate-10">
                  por {{ agents.find(a => a.id === e.performedByAgentId)?.name || `Agente #${e.performedByAgentId}` }}
                </div>
              </div>
              <button
                v-if="hasEventDetails(e)"
                class="flex-shrink-0 px-2.5 py-1 text-[10px] font-medium rounded-md border border-n-brand text-n-brand cursor-pointer transition-colors hover:bg-n-alpha-1 active:bg-n-alpha-2"
                @click="toggleEventDetail(e.id)"
              >{{ timelineExpanded[e.id] ? 'Recolher ▲' : 'Detalhar ▼' }}</button>
            </div>

            <!-- Painel de detalhes (expansível) -->
            <div v-if="timelineExpanded[e.id]" class="px-4 pb-3 pt-2 border-t border-n-weak/50 bg-n-alpha-1">
              <!-- Card Movimentado: mostra de qual etapa para qual -->
              <template v-if="e.action === 'card.stage_changed'">
                <p class="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-n-slate-10">Movimentação de Etapa</p>
                <div class="flex items-center gap-2 flex-wrap">
                  <span class="px-2.5 py-1 text-xs rounded-md bg-n-alpha-2 text-n-slate-12 font-medium border border-n-weak">
                    {{ stageName(e.metadata?.changes?.from_stage_id) }}
                  </span>
                  <span class="text-sm text-n-slate-10 font-bold">→</span>
                  <span class="px-2.5 py-1 text-xs rounded-md text-n-brand font-semibold border border-n-brand/40 bg-n-alpha-1">
                    {{ stageName(e.metadata?.changes?.to_stage_id) }}
                  </span>
                </div>
              </template>
              <!-- Card Alterado: campos do card modificados -->
              <template v-else-if="e.action === 'card.updated'">
                <p class="text-[10px] font-semibold uppercase tracking-wide text-n-slate-10 mb-1">Alteração</p>
                <p class="text-xs text-n-slate-11">Os dados do card foram atualizados.</p>
              </template>
            </div>
          </div>

          <p v-if="!timeline.length" class="text-xs text-center text-n-slate-10 py-8">Nenhum evento registrado.</p>
        </div>

        <!-- Footer -->
        <div class="px-5 py-3 border-t border-n-weak flex-shrink-0 flex justify-end">
          <button
            class="px-3 py-1.5 text-sm rounded border border-n-weak text-n-slate-12 cursor-pointer hover:bg-n-alpha-1"
            @click="timelineModal = null"
          >Fechar</button>
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="scss">
/* Editor de descrição do card kanban */
.kanban-description-editor {
  .ProseMirror-menubar-wrapper {
    .ProseMirror-menubar {
      position: sticky !important;
      top: 0;
      z-index: 10;
      background: var(--color-background-base, #fff);
      border-bottom: 1px solid var(--color-border-default, #e2e8f0);
      padding: 4px 0;
      width: 100% !important;
    }

    .ProseMirror.ProseMirror-woot-style {
      min-height: 200px;
      max-height: 400px;
      overflow-y: auto;
      padding: 8px 4px;
      font-size: 14px !important;
      line-height: 1.6;

      p, li, td { font-size: 14px !important; }
      h2 { font-size: 16px !important; font-weight: 700; margin: 0.75em 0 0.25em; }
      h3 { font-size: 15px !important; font-weight: 600; margin: 0.5em 0 0.2em; }
      p { margin: 0 0 0.5em; }
      ul { list-style: disc; padding-left: 1.25rem; margin: 0.25em 0; }
      ol { list-style: decimal; padding-left: 1.25rem; margin: 0.25em 0; }
      strong { font-weight: 700; }
      em { font-style: italic; }
      code { font-family: monospace; background: rgba(0,0,0,.06); padding: 0 3px; border-radius: 3px; }
      a { color: #2563eb; text-decoration: underline; }
    }
  }
}

.kanban-ghost {
  opacity: 0.35;
  background: var(--color-woot-75, #ede9fe);
  border: 2px dashed var(--color-woot-500, #7c3aed) !important;
  border-radius: 0.5rem;
}

.kanban-dragging {
  transform: rotate(2deg);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
  opacity: 0.95;
}
</style>
